import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BOOKING_ACTIONS } from "../../store/onlineBooking";
import BookingApi from "../../API/Apis/BookingApi";
import {
  AffiliateDetails,
  BookingDetails,
} from "../../@types/Booking/bookingDetails";
import CryptoJS from "crypto-js";
import { BookingGetters } from "../../store/onlineBooking/getters";
import { StateSelectedVehicleDetails } from "../../@types/states/StateSelectedVehicleDetails";
import { TimeInMinutes } from "../../helpers/timerConfig";
import { rentalDays } from "../../helpers/dateManipulators";
import {
  encodeDeliveryUrlParams,
  decodeDeliveryUrlParams,
  persistDeliveryDetails,
} from "../../helpers/deliveryUrlParams";
import { Vehicle } from "../../@types/DTO/Vehicles.d";
import { addMinutes } from "date-fns";
import { useDispatch } from "react-redux";
import { createSearchParams, useNavigate } from "react-router-dom";
import { ShowVehicles } from "../../components/Layouts/ShowVehicles/ShowVehicles";
import {
  ShowVehiclesProps,
  VEHICLE_FILTERS_KEYS,
} from "../../components/Layouts/ShowVehicles/ShowVehiclesProps";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  Drawer,
} from "@mui/material";
import AppButton from "../../components/UI/AppButtons";
import { NoCoverageWarningDialog } from "../../components/UI/AppModal/NoCoverageWarningDialog";
import { t } from "i18next";
import { useSearchParams } from "react-router-dom";
import { BookingUrlParams } from "../../@types/Booking/linkRedirection";
import { useTranslation } from "react-i18next";
import "./Scroller.css";
import { SelectVehicleExtras } from "../../components/Layouts/VehicleExtraSelections/SelectVehicleExtra";
import { ChosenOptions } from "../../components/Layouts/BookingVehicleInfoCard/BookingVehicleInfoCard.props";
import { InsuranceDetails } from "../../@types/DTO/ApiResponses";
import { InsuranceCardsLayout } from "../../components/Layouts/Insurance/Insurance";
import { GretaInsuranceCardsLayout } from "../../components/Layouts/Insurance/GretaInsuranceCardsLayout";
import { VehicleAvailabilityLoader } from "../../components/UI/VehicleAvailabilityLoader/VehicleAvailabilityLoader";
import { VehicleListSkeleton } from "../../components/UI/VehicleSkeletonLoader/VehicleListSkeleton";
import { CardListSkeleton } from "../../components/UI/CardSkeleton/CardSkeleton";
import theme, { formatTheme } from "../../config/MuiColorThemes";
import { format } from "date-fns";
import { SelectedVehicleExtraOptions } from "../../@types/VehicleOptions";
import { PaymentDetailsPage } from "../pay";
import CollapsibleNavbar from "../../components/Layouts/ReservedAreaDashboard/CollapseNavbar";
import BookingSummaryNavbar from "../../components/Layouts/Navbar";
import BookingForm from "../../components/Layouts/BookingForm/BookingForm";
import { PageConfigGetters } from "../../store/PageConfigurations/getters";
import { AppStandardMultiSelectCheckBox } from "../../components/UI/AppMultipleSelection";
import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import FilterAlt from "@mui/icons-material/FilterAlt";
import InsurancePreview from "../../components/Layouts/Insurance/InsurancePreview";
import UpgradeProposals from "../../components/Layouts/UpgradeProposals/UpgradeProposals";
import { UpgradeProposalsResponse } from "../../@types/DTO/UpgradeProposals";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import GretaHeader from "../../components/Layouts/AppHeader/GretaHeader";
import { AllPaymentMethodsResponse } from "../../@types/DTO/AllPaymentMethods";
import { AppInputSwitcher } from "../../components/Layouts/AppInputSwitcher/AppInputSwitcher";
import i18n from "../../config/i18n";
import { AppRadioButton } from "../../components/UI/AppRadioButtons/AppRadioButtons";

function ChooseVehiclePage() {
  const { t: translate } = useTranslation();
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [vehiclesReady, setVehiclesReady] = useState<boolean>(true);
  const [isInsuranceLoading, setIsInsuranceLoading] = useState<boolean>(true);
  const [isResolvingUpgradeOffers, setIsResolvingUpgradeOffers] =
    useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const bookingFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading) setVehiclesReady(false);
  }, [isLoading]);

  const handleEditOpen = () => {
    setIsEditOpen((prev) => !prev);
  };
  const handleCollapseEntered = () => {
    bookingFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };
  const [isEnableHorizontalView, setIsEnableHorizontalView] = useState<
    boolean | null
  >(null);
  const BookingFormConfig = PageConfigGetters.getBookingFormConfig();
  const appSettings = BookingGetters.getAppSettings();
  const recapCardBackgroundColor =
    appSettings && appSettings["extrasBackgroundColor"];
  const filterBackgroundColor =
    appSettings && appSettings["filterBackgroundColor"];
  const secondPageBackgroundColor =
    appSettings && appSettings["secondPageBackgroundColor"];
  const showFilterAtTop = appSettings?.showFilterAtTop;
  const [searchParams] = useSearchParams();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    scrollToTop();
  }, [searchParams]);

  const scriptAffiliate = searchParams.get("scriptAffiliate") ?? "";
  const reservedAreaPayNow = searchParams.get("reservedAreaPayNow");
  sessionStorage.setItem("scriptAffiliate", scriptAffiliate);

  const scriptVoucherCode = searchParams.get("scriptVoucherCode") ?? "";
  sessionStorage.setItem("scriptVoucherCode", scriptVoucherCode);

  const [allVehicles, setAllVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<
    StateSelectedVehicleDetails["vehicleDetails"] | undefined
  >();
  const [cardPaymentChoice, setCardPaymentChoice] = useState<Pick<
    StateSelectedVehicleDetails["paymentDetails"],
    "type" | "price" | "isDiscounted" | "surchargeAmt"
  > | null>(null);
  const bookingInformation: BookingDetails = BookingGetters.getBookingDetails();
  const reduxBookingDetails: BookingDetails =
    BookingGetters.getBookingDetails();
  const [discountValueWithoutVat, setDiscountValueWithoutVat] =
    useState<string>(
      reduxBookingDetails.discountValueWithoutVat
        ? reduxBookingDetails.discountValueWithoutVat
        : "0"
    );
  const navigation = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [pushState, setPushState] = React.useState(0); // pushState is used to store the history pushState for back button functionality

  const [upgradeProposals, setUpgradeProposals] =
    React.useState<UpgradeProposalsResponse | null>(null);
  const [showUpgradeProposals, setShowUpgradeProposals] = React.useState(false);
  const [selectedCancellationUpgrade, setSelectedCancellationUpgrade] =
    React.useState(false);
  const [selectedVehicleUpgradeChoice, setSelectedVehicleUpgradeChoice] =
    React.useState(false);
  const [selectedProtectionUpgrade, setSelectedProtectionUpgrade] =
    React.useState(false);
  const isUpgradeProposalsUsable = (
    proposals: UpgradeProposalsResponse | null | undefined
  ): boolean =>
    proposals?.isUpgradeEnabled !== false &&
    !!(
      proposals?.cancellationUpgrade?.enabled ||
      proposals?.vehicleUpgrade?.enabled ||
      proposals?.protectionUpgrade?.enabled
    );
  const hasUpgradeProposals =
    !!appSettings?.enableUpgradeProposals &&
    isUpgradeProposalsUsable(upgradeProposals);
  const currentSelectedVehicleDetailsFromRedux =
    BookingGetters.getSelectedVehicleDetails();
  const originalInsuranceBeforeUpgradeRef =
    useRef<ChosenOptions["insurance"]>(undefined);
  const upgradeStepCompletedRef = useRef(false);
  const stepTwoNavTokenRef = useRef(0);
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const ReservationDetails = BookingGetters.getBookingDetails();
  const [hasShownInsuranceTab, setHasShownInsuaranceTab] =
    useState<boolean>(false);
  const [priceSortOrder, setPriceSortOrder] = useState<string>("");
  const premiumValue = translate("Premium");
  const priceLowToHighValue = translate("Price: Low to High");
  const priceHighToLowValue = translate("Price: High to Low");
  const [showOnlyPremium, setShowOnlyPremium] = useState<boolean>(false);
  const [activeTab] = useState<string>("0");
  const [selectedFields, setSelectedFields] = useState<ChosenOptions>({
    options: {},
    insurance: undefined,
  });

  const upgradeProposalsFetchRef = useRef<{
    fetchedFor: string;
    inFlight: Promise<void> | null;
    ready: boolean;
  }>({ fetchedFor: "", inFlight: null, ready: false });
  const startUpgradeProposalsFetch = (
    fetchKey: string,
    params: {
      reservationSourceCode: string;
      vehicleGroupCode: string;
      selectedInsuranceId?: string;
    }
  ): Promise<void> => {
    const promise = BookingApi.getUpgradeProposals(params)
      .then((response) => {
        if (upgradeProposalsFetchRef.current.inFlight !== promise) return;
        const proposals = response.data || null;
        if (!proposals) {
          console.warn(
            "getUpgradeProposals returned no data -- no upgrades are configured for this reservation source."
          );
        }
        setUpgradeProposals(proposals);
        upgradeProposalsFetchRef.current.ready =
          isUpgradeProposalsUsable(proposals);
      })
      .catch((error) => {
        if (upgradeProposalsFetchRef.current.inFlight !== promise) return;
        console.error("Failed to fetch upgrade proposals:", error);
        setUpgradeProposals(null);
        upgradeProposalsFetchRef.current.ready = false;
        if (upgradeProposalsFetchRef.current.fetchedFor === fetchKey) {
          upgradeProposalsFetchRef.current.fetchedFor = "";
        }
      })
      .finally(() => {
        if (upgradeProposalsFetchRef.current.inFlight === promise) {
          upgradeProposalsFetchRef.current.inFlight = null;
        }
      });
    upgradeProposalsFetchRef.current.fetchedFor = fetchKey;
    upgradeProposalsFetchRef.current.inFlight = promise;
    return promise;
  };
  useEffect(() => {
    if (
      activeStep !== 1 ||
      !selectedVehicle ||
      !appSettings?.enableUpgradeProposals
    )
      return;
    const reservationSourceCode =
      searchParams.get("reservationSource") || "WEB001";
    const vehicleGroupCode =
      selectedVehicle?.Vehicle?.groupPic?.nationalCode || "";
    const selectedInsuranceId = selectedFields.insurance?.insuranceId || "";
    const fetchKey = `${reservationSourceCode}|${vehicleGroupCode}|${selectedInsuranceId}`;
    if (upgradeProposalsFetchRef.current.fetchedFor === fetchKey) return;
    const cancellationCode =
      upgradeProposals?.cancellationUpgrade?.optionalCode;
    const vehicleUpgradeCode = upgradeProposals?.vehicleUpgrade?.optionalCode;
    if (cancellationCode || vehicleUpgradeCode) {
      setSelectedFields((prevFields) => {
        const options = { ...(prevFields.options || {}) };
        if (cancellationCode) delete options[cancellationCode];
        if (vehicleUpgradeCode) delete options[vehicleUpgradeCode];
        return { ...prevFields, options };
      });
    }
    setSelectedCancellationUpgrade(false);
    setSelectedVehicleUpgradeChoice(false);
    setSelectedProtectionUpgrade(false);
    originalInsuranceBeforeUpgradeRef.current = selectedFields.insurance;
    upgradeStepCompletedRef.current = false;
    startUpgradeProposalsFetch(fetchKey, {
      reservationSourceCode,
      vehicleGroupCode,
      selectedInsuranceId: selectedInsuranceId || undefined,
    });
  }, [
    activeStep,
    selectedVehicle,
    selectedFields.insurance?.insuranceId,
    searchParams,
    appSettings?.enableUpgradeProposals,
  ]);
  const waitForUpgradeProposals = async (): Promise<boolean> => {
    if (upgradeProposalsFetchRef.current.inFlight) {
      await upgradeProposalsFetchRef.current.inFlight;
    }
    return upgradeProposalsFetchRef.current.ready;
  };
  const goToStepTwo = async () => {
    const token = ++stepTwoNavTokenRef.current;
    setIsResolvingUpgradeOffers(true);
    upgradeStepCompletedRef.current = false;
    const shouldShow = await waitForUpgradeProposals();
    if (!isMountedRef.current || stepTwoNavTokenRef.current !== token) return;
    setIsResolvingUpgradeOffers(false);
    setActiveStep(2);
    window.history.pushState({ step: 2 }, "Step 2", "");
    setShowUpgradeProposals(shouldShow);
  };

  const [userId] = useState(localStorage.getItem("userID"));
  const [discount, setDiscount] = useState<number>(0);
  const isValidUserId =
    userId != null &&
    userId !== "null" &&
    userId !== "undefined" &&
    userId !== "";
  const scriptChan = searchParams.get("scriptChannel") ?? "";
  const [scriptChannel, setScriptChannel] = useState<string>(scriptChan);
  const isInvalidScriptChannelResSource =
    searchParams.get("isInvalidScriptChannelResSource") == "true"
      ? true
      : false;
  window.name = scriptChannel; //storing the scriptChannel even when page get refreshed
  sessionStorage.setItem("scriptChannel", scriptChannel);
  const vehicleCodeFromUrl = searchParams.get("vehicleInternationalCode");
  const handleEdit = () => {
    setIsNotClickedEdit(false);
    setIsLoading(false);
    if (vehicleCodeFromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete("vehicleInternationalCode");
      window.history.replaceState({}, "", url.toString());
    }
    if (scriptChannel && (!userId || userId == "")) {
      navigation(`/embed?scriptChannel=${scriptChannel}`);
    } else if (scriptAffiliate) {
      navigation(`/embed?scriptAffiliate=${scriptAffiliate}`);
    } else if (scriptVoucherCode) {
      navigation(`/embed?scriptVoucherCode=${scriptVoucherCode}`);
    } else {
      navigation("/embed");
    }
  };
  const [isNotclickedEdit, setIsNotClickedEdit] = useState(true);
  const type = searchParams.get("reservationSourcePayLater") === "true";
  const [paymentType, setPaymentType] = useState<"payNow" | "payLater">(
    type ? "payLater" : "payNow"
  ); //payment type selected either in first step or in payment page via cards
  useEffect(() => {
    BookingApi.getAppSetting().then((e: any) => {
      setIsEnableHorizontalView(e.enableHorizontalView);
    });
  }, []);

  useEffect(() => {
    if (isValidUserId) {
      setDiscount(0);
      setScriptChannel("");
      window.name = "";
      sessionStorage.setItem("scriptChannel", "");
    } else {
      setDiscount((100 - (selectedVehicle?.Vehicle?.payNowDis || 0)) / 100);
    }
  }, [userId, selectedVehicle?.Vehicle?.payNowDis]);

  const [totalPrice, setTotalPrice] = useState<number>(
    searchParams.get("reservationSourcePayLater") === "true" ||
      (userId != null && userId != "null" && userId != "")
      ? parseFloat(
          (selectedVehicle?.Reference?.ID && selectedVehicle?.Reference?.ID > 0
            ? (
                selectedVehicle?.Reference?.originalAmount -
                selectedVehicle?.Reference.ID
              ).toString()
            : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) || "0"
        )
      : parseFloat(
          (selectedVehicle?.Reference?.ID && selectedVehicle?.Reference?.ID > 0
            ? (
                selectedVehicle?.Reference?.originalAmount -
                selectedVehicle?.Reference.ID
              ).toString()
            : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) || "0"
        ) * discount
  );
  const [insuranceList, setInsuranceList] = useState<InsuranceDetails>({
    "Booking Insurance": [],
    Specification: [],
  });
  const [groupName, setGroupName] = useState<string>("");
  const [errorMsgNoVehicle, setErrorMsgNoVehicle] = useState<string>("");
  const [showInfo, setShowInfo] = useState(false);
  const toogleVehicleInfoCardShowingStatus = () => {
    setShowInfo(!showInfo);
  };
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [reservedArea, setReservedArea] = useState(false);
  const [isScalapayExpanded, setIsScalapayExpanded] = useState<boolean>(false);

  const enableFilterBySeat = appSettings && appSettings["enableFilterBySeat"];
  const redirectUrl = appSettings && appSettings["redirectUrl"];
  const enableFilterByFuel = appSettings && appSettings["enableFilterByFuel"];
  const enableFilterByTransmission =
    appSettings && appSettings["enableFilterByTransmission"];
  const [extrasPrice, setExtrasPrice] = useState(0); // extras and insurance price
  const [onlyExtrasPrice, setOnlyExtrasPrice] = useState<number>(0); // extras price
  const [insurancePrice, setInsurancePrice] = useState<number>(0); // insurance price
  const [vehiclePrice, setVehiclePrice] = useState<number>(0); // vehicle price
  const [showNoCoverageWarning, setShowNoCoverageWarning] = useState(false);
  const pendingProceedTypeRef = useRef<string>("");
  const autoProceedAfterAddRef = useRef(false);
  const [noCoverageWarningTitle, setNoCoverageWarningTitle] =
    useState<string>("");
  const [noCoverageWarningBody, setNoCoverageWarningBody] =
    useState<string>("");
  const [noCoverageWarningContinueCta, setNoCoverageWarningContinueCta] =
    useState<string>("");
  const [noCoverageWarningAddCta, setNoCoverageWarningAddCta] =
    useState<string>("");
  const [showPriceDetail, setShowPriceDetail] = useState(false);
  const [showInsurancePreview, setShowInsurancePreview] = useState(
    appSettings?.showInsurancePreview
  );
  const enableFilterByPrice = appSettings?.enableFilterByPrice;
  const enableFilterByPremium = appSettings?.enableFilterByPremium;
  const [areFiltersActive, setAreFiltersActive] = useState<boolean>(false);

  const updateShowInsurance = () => {
    if (appSettings?.showInsurancePreview) {
      setSelectedFields((prevState) => ({
        ...prevState,
        insurance: undefined,
      }));
      setShowInsurancePreview(false);
    }
  };
  // ----------- Filters  -------------------
  const [tempAvailabilityFilter, setTempAvailabilityFilter] = useState<
    ShowVehiclesProps["vehiclesFilter"]
  >({
    Transmission: {},
    status: {},
    category: {},
    type: {},
    TotalSeats: [],
    MacroGroup: {},
  });
  const [filterValue, setFilterValue] = useState<VEHICLE_FILTERS_KEYS>({
    Transmission: {},
    status: {},
    category: {},
    type: {},
    TotalSeats: [],
    MacroGroup: {},
  });
  const [pickUpLabel, setPickUpLabel] = useState<string>("");
  const [dropOffLabel, setDropOffLabel] = useState<string>("");
  const [transmissionLabel, setTransmissionLabel] = useState<string>("");
  const [seatsLabel, setSeatsLabel] = useState<string>("");
  const [fuelLabel, setFuelLabel] = useState<string>("");
  const [vehiclePageTitle, setVehiclePageTitle] = useState<string>("");
  const [priceDetailsText, setPriceDetailsText] = useState<string>("");
  const [whichProtectionPackageText, setWhichProtectionPackageText] =
    useState<string>("");
  const lang = i18n.language;
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const data = textResource?.[lang]?.flatMap((item) => item.data || []) || [];
  useEffect(() => {
    const pickUpLabelData = data.find(
      (item) => item.key === "PickUpLocationLabel"
    );
    const DropOffLabelData = data.find(
      (item) => item.key === "DropOffLocationLabel"
    );
    const transmissionData = data.find(
      (item) => item.key === "transmissionLabel"
    );
    const seatsData = data.find((item) => item.key === "seatsLabel");
    const fuelData = data.find((item) => item.key === "fuelLabel");
    const vehiclePageTitleData = data.find((item) => item.key === "carToDrive");
    setPickUpLabel(pickUpLabelData?.body || translate("Pick Up"));
    setDropOffLabel(DropOffLabelData?.body || translate("Drop Off"));
    setTransmissionLabel(transmissionData?.body || translate("Transmission"));
    setSeatsLabel(seatsData?.body || translate("No. of Seats"));
    setFuelLabel(fuelData?.body || translate("Type of Fuel"));
    setVehiclePageTitle(
      vehiclePageTitleData?.body || translate("Which car do you want to drive?")
    );
    setPriceDetailsText(
      data.find((item) => item.key === "Price details")?.body ||
        translate("Price details")
    );
    setWhichProtectionPackageText(
      data.find((item) => item.key === "selectProtectionPackage")?.body ||
        translate("WHICH PROTECTION PACKAGE DO YOU NEED?")
    );
    setNoCoverageWarningTitle(
      data.find((item) => item.key === "noCoverageWarningTitle")?.body ||
        translate("Do you want to continue without coverage?")
    );
    setNoCoverageWarningBody(
      data.find((item) => item.key === "noCoverageWarningBody")?.body ||
        translate(
          "You have not selected any paid coverage. You can continue without coverage or add the most complete package now."
        )
    );
    setNoCoverageWarningContinueCta(
      data.find((item) => item.key === "noCoverageWarningContinueCta")?.body ||
        translate("Continue without coverage")
    );
    setNoCoverageWarningAddCta(
      data.find((item) => item.key === "noCoverageWarningAddCta")?.body ||
        translate("Add {coverageName} and continue")
    );
  }, [lang, textResource, translate]);
  const [filterfuelTypeData, setFilterfuelTypeData] = useState([]);
  const [filterseatData, setFilterseatData] = useState([]);
  const [filtertransmissionData, setFiltertransmissionData] = useState([]);
  const transmissionHeader =
    filtertransmissionData != null && filtertransmissionData.length > 0
      ? transmissionLabel
      : "";
  const seatHeader =
    filterseatData != null && filterseatData.length > 0 ? seatsLabel : "";
  const fuelTypeHeader =
    filterfuelTypeData != null && filterfuelTypeData.length > 0
      ? fuelLabel
      : "";
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilterValue(tempAvailabilityFilter);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [tempAvailabilityFilter]);
  const updateActiveStep = () => {
    setShowUpgradeProposals(false);
    setActiveStep(0);
    setPushState(0);
    scrollToTop();
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    BookingApi.getBookingFilterMethods().then((response) => {
      const filterMethods = response.data;
      setFilterfuelTypeData(filterMethods?.fuelType);
      const seat = filterMethods?.noOfSeatSelect;
      const sortedSeatData = [...seat].sort((a, b) => a - b);
      setFilterseatData(sortedSeatData);
      setFiltertransmissionData(filterMethods?.transmission);
    });
  }, []);

  const hideIframe = () => {
    window.parent.postMessage(
      {
        event_id: "hideIframe",
      },
      "*"
    );
  };

  const localeScalapay = (() => {
    try {
      const lang = i18n.language?.split("-")[0];
      return lang || "it";
    } catch (e) {
      return "it";
    }
  })();
  const updateSelectedPrice = (price) => {
    const parsedPrice = parseFloat(price);
    if (!isNaN(parsedPrice)) {
      setSelectedPrice(parsedPrice);
    }
  };
  const selectVehicleExtrasRef = useRef<HTMLDivElement>(null); //this is used so that for mobile view if insurance selected then extra componet will be scrolled to top
  // // -----------------------------Code for browser back button functionality----------------
  useEffect(() => {
    const state = { step: pushState };
    window.history.pushState(state, `Step ${pushState}`, "");
  }, [pushState]);

  useEffect(() => {
    const handlePopState = (event) => {
      stepTwoNavTokenRef.current += 1;
      setIsResolvingUpgradeOffers(false);
      if (event.state && event.state.step !== undefined) {
        if (event.state.step === 2) {
          setShowUpgradeProposals(
            upgradeProposalsFetchRef.current.ready &&
              !upgradeStepCompletedRef.current
          );
        } else {
          setShowUpgradeProposals(false);
        }
        setActiveStep(event.state.step);
      } else {
        console.log(
          "In else block for history push state where state is undefined"
        );
        setShowUpgradeProposals(false);
        setActiveStep(0);
      }
    };
    window.addEventListener("popstate", handlePopState);
    if (window.history.state && window.history.state.step !== undefined) {
      setActiveStep(window.history.state.step);
    }
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);
  function VehicleFilters() {
    return (
      <>
        {(enableFilterByFuel && filterfuelTypeData.length > 0) ||
        (enableFilterBySeat && filterseatData.length > 0) ||
        (enableFilterByTransmission && filtertransmissionData.length > 0) ||
        enableFilterByPrice ||
        enableFilterByPremium ? (
          <h2 className="font-bold text-xl text-gray-600 pb-8 box-border">
            {t("Filter to Vehicles")}
          </h2>
        ) : null}
        <div
          className={
            "h-full overflow-hidden hover:overflow-auto flex flex-col box-border px-4"
          }
        >
          {enableFilterByFuel && (
            <AppStandardMultiSelectCheckBox
              value={Object.values(tempAvailabilityFilter?.type || {})}
              onChange={async (e) => {
                const type1 = (e || []).reduce((prev, current) => {
                  prev[current.toLowerCase()] = current;
                  return prev;
                }, {});
                setTempAvailabilityFilter({
                  type: type1,
                  category: tempAvailabilityFilter?.category || {},
                  status: tempAvailabilityFilter?.status || {},
                  TotalSeats: tempAvailabilityFilter?.TotalSeats || [],
                  MacroGroup: tempAvailabilityFilter?.MacroGroup || {},
                  Transmission: tempAvailabilityFilter?.Transmission || {},
                });
              }}
              header={fuelTypeHeader}
              options={filterfuelTypeData.map((fuelType) => {
                return {
                  label: translate(fuelType),
                  value: translate(fuelType),
                };
              })}
              color={"primary"}
            />
          )}

          {enableFilterBySeat && (
            <AppStandardMultiSelectCheckBox
              value={tempAvailabilityFilter?.TotalSeats || []}
              color={"primary"}
              onChange={async (e) => {
                setTempAvailabilityFilter({
                  type: tempAvailabilityFilter?.type || {},
                  category: tempAvailabilityFilter?.category || {},
                  status: tempAvailabilityFilter?.status || {},
                  TotalSeats: e || [],
                  MacroGroup: tempAvailabilityFilter?.MacroGroup || {},
                  Transmission: tempAvailabilityFilter?.Transmission || {},
                });
              }}
              header={seatHeader}
              options={filterseatData.map((seats) => {
                return {
                  label: `${translate(seats)} ${t("seats")}`,
                  value: seats,
                };
              })}
            />
          )}

          {enableFilterByTransmission && (
            <AppStandardMultiSelectCheckBox
              value={Object.values(tempAvailabilityFilter?.Transmission || {})}
              color={"primary"}
              onChange={async (e) => {
                setTempAvailabilityFilter({
                  type: tempAvailabilityFilter?.type || {},
                  category: tempAvailabilityFilter?.category || {},
                  status: tempAvailabilityFilter?.status || {},
                  TotalSeats: tempAvailabilityFilter?.TotalSeats || [],
                  MacroGroup: tempAvailabilityFilter?.MacroGroup || {},
                  Transmission: (e || []).reduce((prev, current) => {
                    prev[current.toLowerCase()] = current;
                    return prev;
                  }, {}),
                });
              }}
              header={transmissionHeader}
              options={filtertransmissionData.map((transmission) => ({
                label: translate(transmission),
                value: transmission,
              }))}
            />
          )}

          {enableFilterByPrice && (
            <AppRadioButton
              required={false}
              label={t("Sort By Price")}
              value={priceSortOrder || ""}
              options={[
                { label: t("Price: High to Low"), value: priceHighToLowValue },
                { label: t("Price: Low to High"), value: priceLowToHighValue },
              ]}
              onChange={(e) => setPriceSortOrder(e)}
              color="primary"
            />
          )}

          {enableFilterByPremium && (
            <AppStandardMultiSelectCheckBox
              value={showOnlyPremium ? [premiumValue] : []}
              color="primary"
              onChange={(e) => {
                setShowOnlyPremium(e?.includes(premiumValue) || false);
              }}
              header={translate("Premium Brand")}
              options={[{ label: translate("Premium"), value: premiumValue }]}
            />
          )}
        </div>
      </>
    );
  }

  function VehicleFiltersAtTop() {
    return (
      <div
        className="w-full block rounded py-2 px-2"
        style={{
          background: filterBackgroundColor ? filterBackgroundColor : "",
        }}
      >
        <div
          className="flex flex-row flex-wrap justify-center items-center gap-4"
          style={{
            background: filterBackgroundColor ? filterBackgroundColor : "",
          }}
        >
          {(enableFilterByFuel && filterfuelTypeData.length > 0) ||
          (enableFilterBySeat && filterseatData.length > 0) ||
          (enableFilterByTransmission && filtertransmissionData.length > 0) ||
          enableFilterByPremium ||
          enableFilterByPrice ? (
            <span className="font-bold text-sm text-gray-600 whitespace-nowrap">
              {t("Filtra Per")}
            </span>
          ) : null}
          {enableFilterByFuel && (
            <span className="min-w-[180px]">
              <AppInputSwitcher
                required={false}
                className="w-full bg-white"
                type="select"
                variant="with-search"
                containerheight="40px"
                label={translate("Fuel")}
                options={[
                  { label: translate("Select Fuel"), value: "" },
                  ...filterfuelTypeData.map((fuelType) => ({
                    label: translate(fuelType),
                    value: translate(fuelType),
                  })),
                ]}
                onchange={async (e) => {
                  const selectedValues = Array.isArray(e) ? e : [e];
                  const type1 = selectedValues.reduce((prev, current) => {
                    prev[current.toLowerCase()] = current;
                    return prev;
                  }, {});
                  setTempAvailabilityFilter({
                    type: type1,
                    category: tempAvailabilityFilter?.category || {},
                    status: tempAvailabilityFilter?.status || {},
                    TotalSeats: tempAvailabilityFilter?.TotalSeats || [],
                    MacroGroup: tempAvailabilityFilter?.MacroGroup || {},
                    Transmission: tempAvailabilityFilter?.Transmission || {},
                  });
                }}
                value={Object.values(tempAvailabilityFilter?.type || {})[0]}
              />
            </span>
          )}

          {enableFilterBySeat && (
            <span className="min-w-[160px]">
              <AppInputSwitcher
                required={false}
                className="w-full bg-white"
                containerheight="40px"
                type="select"
                variant="with-search"
                label={translate("seats")}
                options={[
                  { label: translate("Select Seat"), value: [] },
                  ...filterseatData.map((seats) => ({
                    label: `${translate(seats)} ${translate("seats")}`,
                    value: seats,
                  })),
                ]}
                value={
                  tempAvailabilityFilter?.TotalSeats &&
                  tempAvailabilityFilter.TotalSeats.length > 0
                    ? Object.values(tempAvailabilityFilter.TotalSeats)[0] +
                      " " +
                      translate("seats")
                    : ""
                }
                onchange={async (e) => {
                  const selectedSeats = Array.isArray(e) ? e : [e];
                  setTempAvailabilityFilter({
                    type: tempAvailabilityFilter?.type || {},
                    category: tempAvailabilityFilter?.category || {},
                    status: tempAvailabilityFilter?.status || {},
                    TotalSeats: selectedSeats || [],
                    MacroGroup: tempAvailabilityFilter?.MacroGroup || {},
                    Transmission: tempAvailabilityFilter?.Transmission || {},
                  });
                }}
              />
            </span>
          )}

          {enableFilterByTransmission && (
            <span className="min-w-[180px]">
              <AppInputSwitcher
                required={false}
                className="w-full bg-white"
                containerheight="40px"
                type="select"
                variant="with-search"
                label={translate("Transmission")}
                options={[
                  { label: translate("Select Transmission"), value: "" },
                  ...filtertransmissionData.map((transmission) => ({
                    label: translate(transmission),
                    value: transmission,
                  })),
                ]}
                value={(() => {
                  const rawVal = Object.values(
                    tempAvailabilityFilter?.Transmission || {}
                  )[0] as string;

                  return rawVal ? translate(rawVal) : "";
                })()}
                onchange={async (e) => {
                  const transmission = (Array.isArray(e) ? e : [e]).reduce(
                    (prev, current) => {
                      prev[current.toLowerCase()] = current;
                      return prev;
                    },
                    {}
                  );
                  setTempAvailabilityFilter({
                    type: tempAvailabilityFilter?.type || {},
                    category: tempAvailabilityFilter?.category || {},
                    status: tempAvailabilityFilter?.status || {},
                    TotalSeats: tempAvailabilityFilter?.TotalSeats || [],
                    MacroGroup: tempAvailabilityFilter?.MacroGroup || {},
                    Transmission: transmission,
                  });
                }}
              />
            </span>
          )}
          {enableFilterByPrice && (
            <span className="min-w-[200px]">
              <AppInputSwitcher
                required={false}
                className="w-full bg-white"
                containerheight="40px"
                type="select"
                variant="with-search"
                label={translate("Sort By Price")}
                options={[
                  {
                    label: translate("Sort By Price"),
                    value: "",
                  },
                  {
                    label: translate("Price: High to Low"),
                    value: priceHighToLowValue,
                  },
                  {
                    label: translate("Price: Low to High"),
                    value: priceLowToHighValue,
                  },
                ]}
                value={priceSortOrder || ""}
                onchange={(e) => {
                  const selected = Array.isArray(e) ? e[0] : e;
                  setPriceSortOrder(selected);
                }}
              />
            </span>
          )}

          {enableFilterByPremium && (
            <span className="min-w-[200px]">
              <AppInputSwitcher
                required={false}
                className="w-full bg-white"
                containerheight="40px"
                type="select"
                variant="with-search"
                label={translate("Premium Brand")}
                options={[
                  {
                    label: translate("All Vehicles"),
                    value: "",
                  },
                  {
                    label: translate("Premium"),
                    value: premiumValue,
                  },
                ]}
                value={showOnlyPremium ? premiumValue : ""}
                onchange={(e: any) => {
                  const selected = Array.isArray(e) ? e[0] : e;
                  setShowOnlyPremium(selected === premiumValue);
                }}
              />
            </span>
          )}
        </div>
      </div>
    );
  }

  // --------------- END FILTER --------------------------
  useEffect(() => {
    if (userId != null && userId != "null" && userId != "") {
      setReservedArea(true);
    } else {
      setReservedArea(false);
    }
  }, [userId]);
  useEffect(() => {
    setHasShownInsuaranceTab(hasShownInsuranceTab || activeTab == "1");
  }, [activeTab]);

  const [resSourceFound, setResSourceFound] = useState(false);
  useEffect(() => {
    let price: number = parseFloat(
      selectedFields.insurance?.price?.toString() || "0"
    );
    setInsurancePrice(price);
    let selectedExtras: number = parseFloat("0");
    if (selectedFields.options) {
      Object.values<SelectedVehicleExtraOptions>(
        selectedFields.options || {}
      ).map((el) => {
        price += el.price * el.quantity;
        selectedExtras += el.price * el.quantity;
      });
    }
    setExtrasPrice(price);
    let totalPrice;
    if (cardPaymentChoice) {
      totalPrice = cardPaymentChoice.price;
    } else if (
      searchParams.get("reservationSourcePayLater") === "true" ||
      (userId != null && userId != "null" && userId != "")
    ) {
      totalPrice = parseFloat(
        (selectedVehicle?.Reference?.ID && selectedVehicle?.Reference?.ID > 0
          ? (
              selectedVehicle?.Reference?.originalAmount -
              selectedVehicle?.Reference.ID
            ).toString()
          : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) || "0"
      );
    } else {
      totalPrice =
        parseFloat(
          (selectedVehicle?.Reference?.ID && selectedVehicle?.Reference?.ID > 0
            ? (
                selectedVehicle?.Reference?.originalAmount -
                selectedVehicle?.Reference.ID
              ).toString()
            : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) || "0"
        ) * discount;
    }
    const totalPriceAfterExtra = price + totalPrice;
    setOnlyExtrasPrice(selectedExtras);
    setVehiclePrice(totalPrice);
    setTotalPrice(totalPriceAfterExtra);
    // BOOKING-996: keep the recap's bold "Totale" line (selectedPrice) in
    // sync here too, instead of only on the step's Continue-button click --
    // otherwise it kept showing the stale Pay Now total until Continue was
    // pressed, even after the card's Pay at Pickup choice updated everything
    // else above it.
    setSelectedPrice(totalPriceAfterExtra);
  }, [
    selectedFields,
    selectedVehicle?.TotalCharge?.RateTotalAmount,
    discount,
    cardPaymentChoice,
  ]);

  const updateVehiclePrice = (price) => {
    const totalPriceAfterExtra = insurancePrice + onlyExtrasPrice + price;
    setTotalPrice(totalPriceAfterExtra);
    setVehiclePrice(price);
  };
  const updateVehiclePriceLater = (price: number): void => {
    let totalPriceAfterExtra = insurancePrice + onlyExtrasPrice + price;
    const payLaterSurchargePercent =
      selectedVehicle?.Vehicle?.payLaterSurcharge ?? 0;

    const surchargeAmount =
      (totalPriceAfterExtra * payLaterSurchargePercent) / 100;

    totalPriceAfterExtra += surchargeAmount;
    setTotalPrice(totalPriceAfterExtra);
    setVehiclePrice(price);
  };

  const hideThirdTab =
    appSettings?.donotShowInsurance && appSettings?.donotShowExtras;
  const [hideInsExtrasThirdTab, setHideInsExtrasThirdTab] = useState<boolean>(
    hideThirdTab ?? false
  );
  let hideTab = hideThirdTab;
  const [reservationSourcePayNow, setReservationSourcePayNow] =
    useState<string>("false");
  const isAirport = searchParams.get("isAirport");
  const [reservationSourcePayLater, setReservationSourcePayLater] =
    useState<string>("false");
  useEffect(() => {
    setIsLoading(true);
    const t: BookingUrlParams = {
      dropOffDateAndTime: searchParams.get("dropOffDateAndTime") || "",
      pickUpDateAndTime: searchParams.get("pickUpDateAndTime") || "",
      pickUpLocation: searchParams.get("pickUpLocation") || "",
      dropOffLocation: searchParams.get("dropOffLocation") || "",
      pickUpLocationName: searchParams.get("pickUpLocationName") || "",
      dropOffLocationName: searchParams.get("dropOffLocationName") || "",
      reservationSource: searchParams.get("reservationSource") || "WEB001",
      macroGroupName: searchParams.get("macroGroupName") || " ",
      discountValueWithoutVat:
        searchParams.get("discountValueWithoutVat") || " ",
      showPics: searchParams.get("showPics") || "true",
      showOptionalImage: searchParams.get("showOptionalImage") || "true",
      showVehicleParameter: searchParams.get("showVehicleParameter") || "true",
      showBookingDiscount: searchParams.get("showBookingDiscount") || "true",
      agreementCoupon: searchParams.get("agreementCoupon") || " ",
      showVehicleExtraImage:
        searchParams.get("showVehicleExtraImage") || "true",
      isYoungDriverAge: searchParams.get("isYoungDriverAge") || "",
      isYoungDriver2Age: searchParams.get("isYoungDriver2Age") || "",
      isSeniorDriverAge: searchParams.get("isSeniorDriverAge") || "",
      noFeeAge: searchParams.get("noFeeAge") || "",
      isAgeLimitSelected: searchParams.get("isAgeLimitSelected") || "",
      reservationSourcePayNow:
        searchParams.get("reservationSourcePayNow") || "false",
      reservationSourcePayLater:
        searchParams.get("reservationSourcePayLater") || "false",
      youngDriverAgeValue: searchParams.get("youngDriverAgeValue") || "",
      youngDriver2AgeValue: searchParams.get("youngDriver2AgeValue") || "",
      seniorDriverAgeValue: searchParams.get("seniorDriverAgeValue") || "",
      noAgeDriverValue: searchParams.get("noAgeDriverValue") || "",
      affiliateName: searchParams.get("affiliateName") || "",
      affiliateId: searchParams.get("affiliateId") || "",
    };
    for (const i in t) {
      if (!t[i]) {
        navigation("/");
      }
    }
    // rebuild the home delivery/return inputs from the URL so the
    // refresh-time quotation re-fetch returns the per-vehicle surcharge again.
    // Kept out of `t` above so the empty-key redirect check ignores them.
    const deliveryFromUrl = decodeDeliveryUrlParams(searchParams);
    setReservationSourcePayNow(
      searchParams.get("reservationSourcePayNow") || ""
    );
    setReservationSourcePayLater(
      searchParams.get("reservationSourcePayLater") || ""
    );

    const currentBookingDetails: BookingDetails = {
      pickUpDateAndTime: new Date(parseInt(t.pickUpDateAndTime)).getTime(),
      dropOffDateAndTime: new Date(parseInt(t.dropOffDateAndTime)).getTime(),
      dropOffLocationDetails: {
        name: "",
        code: t.dropOffLocation,
        locationNumber: "",
        locationAddress: "",
        locationCity: "",
        zipCode: "",
        publicWebDescription: "",
        publicWebDescriptionEN: "",
      },
      pickUpLocationDetails: {
        name: "",
        code: t.pickUpLocation,
        locationNumber: "",
        locationAddress: "",
        locationCity: "",
        zipCode: "",
        publicWebDescription: "",
        publicWebDescriptionEN: "",
      },
      reservationSource: {
        name: "",
        code: t.reservationSource || "",
        locationNumber: "",
        locationAddress: "",
        locationCity: "",
        zipCode: "",
        publicWebDescription: "",
        publicWebDescriptionEN: "",
      },
      macroGroupName: t.macroGroupName || "",
      showPics: t.showPics == "true",
      showOptionalImage: t.showOptionalImage == "true",
      showVehicleParameter: t.showVehicleParameter == "true",
      showVehicleExtraImage: t.showVehicleExtraImage == "true",
      showBookingDiscount: t.showBookingDiscount == "true",
      agreementCoupon: t.agreementCoupon || "",
      discountValueWithoutVat: t.discountValueWithoutVat || "",
      isYoungDriverAge: t.isYoungDriverAge || "",
      isYoungDriver2Age: t.isYoungDriver2Age || "",
      isSeniorDriverAge: t.isSeniorDriverAge || "",
      noFeeAge: t.noFeeAge || "",
      isAgeLimitSelected: t.isAgeLimitSelected || "",
      dropOffLocationName: t.dropOffLocationName || "",
      pickUpLocationName: t.pickUpLocationName || "",
      isPayLaterFee: false,
      payLaterFeeAmt: "",
      youngDriverAgeValue: t.youngDriverAgeValue || "",
      youngDriver2AgeValue: t.youngDriver2AgeValue || "",
      seniorDriverAgeValue: t.seniorDriverAgeValue || "",
      noAgeDriverValue: t.noAgeDriverValue || "",
      affiliateDetails: {
        name: t.affiliateName,
        code: t.affiliateId || "",
      },
    };
    const userId: any = localStorage.getItem("userID");
    if (userId != null && userId != "null" && userId != "") {
      BookingApi.getReservationLocations()
        .then((locationResponse) => {
          const dropOffLocation = locationResponse.data.find(
            (location) => location.locationCode == t.dropOffLocation
          );
          const pickUpLocation = locationResponse.data.find(
            (location) => location.locationCode == t.pickUpLocation
          );
          if (!pickUpLocation || !dropOffLocation) {
            navigation("/");
            return Promise.reject(new Error("Invalid location"));
          }
          currentBookingDetails.pickUpLocationDetails = {
            reservationSourcePayLater:
              searchParams.get("reservationSourcePayLater") === "true",
            reservationSourcePayNow:
              searchParams.get("reservationSourcePayLater") === "true",
            name: pickUpLocation?.locationName || dropOffLocation?.locationName,
            code: pickUpLocation?.locationCode || dropOffLocation?.locationCode,
            locationAddress:
              pickUpLocation?.locationAddress ||
              dropOffLocation?.locationAddress,
            locationCity:
              pickUpLocation?.locationCity || dropOffLocation?.locationCity,
            locationNumber:
              pickUpLocation?.locationNumber || dropOffLocation?.locationNumber,
            publicWebDescription:
              pickUpLocation?.publicWebDescription ||
              dropOffLocation?.publicWebDescription,
            publicWebDescriptionEN:
              pickUpLocation?.publicWebDescriptionEN ||
              dropOffLocation?.publicWebDescriptionEN,
            zipCode: pickUpLocation?.zipCode || dropOffLocation?.zipCode,
          };
          currentBookingDetails.dropOffLocationDetails = {
            reservationSourcePayLater:
              searchParams.get("reservationSourcePayLater") === "true",
            reservationSourcePayNow:
              searchParams.get("reservationSourcePayLater") === "true",
            locationAddress: dropOffLocation?.locationAddress,
            locationCity: dropOffLocation?.locationCity,
            locationNumber: dropOffLocation?.locationNumber,
            publicWebDescription: dropOffLocation?.publicWebDescription,
            publicWebDescriptionEN: dropOffLocation?.publicWebDescriptionEN,
            zipCode: dropOffLocation?.zipCode,
            name: dropOffLocation?.locationName,
            code: dropOffLocation?.locationCode,
          };
          currentBookingDetails.dropAtSameLocation =
            pickUpLocation.locationCode == dropOffLocation.locationCode;
          return BookingApi.getReservationSourceForOnlineUser(userId);
        })
        .then((reservationSourcesResponse) => {
          const reservationSource =
            reservationSourcesResponse?.data?.reservationSourceList?.find(
              (source) => {
                return source.code == t.reservationSource;
              }
            );

          if (reservationSource) {
            setResSourceFound(true);
            currentBookingDetails.reservationSource = {
              reservationSourcePayLater: reservationSource.payLater ?? false,
              reservationSourcePayNow: reservationSource.payNow ?? false,
              locationAddress: "",
              locationCity: "",
              locationNumber: "",
              publicWebDescription: "",
              publicWebDescriptionEN: "",
              zipCode: "",
              name: reservationSource["description"],
              code: reservationSource.code,
            };
          }

          const deliverySource =
            deliveryFromUrl.homeDeliverySelected ||
            deliveryFromUrl.homeReturnSelected
              ? deliveryFromUrl
              : reduxBookingDetails;
          const bookingDetailsWithDelivery = {
            ...currentBookingDetails,
            homeDeliverySelected: deliverySource.homeDeliverySelected,
            homeReturnSelected: deliverySource.homeReturnSelected,
            homeDeliveryAddress: deliverySource.homeDeliveryAddress,
            homeReturnAddress: deliverySource.homeReturnAddress,
            deliveryCoordinates: deliverySource.deliveryCoordinates,
            returnCoordinates: deliverySource.returnCoordinates,
            homeDeliveryAddressKm: deliverySource.homeDeliveryAddressKm,
            homeReturnAddressKm: deliverySource.homeReturnAddressKm,
          };
          dispatch(
            BOOKING_ACTIONS.setBookingInformation(bookingDetailsWithDelivery)
          );
          return BookingApi.getReservationVehicles(bookingDetailsWithDelivery);
        })
        .then((vehiclesResponse) => {
          //the below code is written to find if the vehicle shared via url is available or not
          const urlParams = new URLSearchParams(window.location.search);
          const vehicleInternationalCode = urlParams.get(
            "vehicleInternationalCode"
          );
          const vehicles = vehiclesResponse?.data?.data?.Vehicles || [];
          const matchedVehicle = vehicles.find(
            (v) => v?.Vehicle?.Code === vehicleInternationalCode
          );

          if (!matchedVehicle) {
            setIsVehicleUnavQuot(true); // <-- your variable
          }
          dispatch(
            BOOKING_ACTIONS.setSessionExpiryTime(
              addMinutes(new Date(), TimeInMinutes).getTime()
            )
          );
          setErrorMsgNoVehicle(
            vehiclesResponse.data?.data?.errors?.Error?.ShortText
          );
          setAllVehicles([...(vehiclesResponse.data?.data?.Vehicles || [])]);
        })
        .catch((error) => {
          console.error(error);
          navigation("/");
        })
        .finally(() => {
          setActiveStep(0);
          setPushState(0);
          setIsLoading(false);
        });
    } else if (!resSourceFound) {
      BookingApi.getReservationLocations()
        .then((locationResponse) => {
          const dropOffLocation = locationResponse.data.find(
            (location) => location.locationCode == t.dropOffLocation
          );
          const pickUpLocation = locationResponse.data.find(
            (location) => location.locationCode == t.pickUpLocation
          );
          if (!pickUpLocation || !dropOffLocation) {
            navigation("/");
            return Promise.reject(new Error("Invalid location"));
          }
          currentBookingDetails.pickUpLocationDetails = {
            reservationSourcePayLater:
              searchParams.get("reservationSourcePayLater") === "true",
            reservationSourcePayNow:
              searchParams.get("reservationSourcePayLater") === "true",
            name: pickUpLocation?.locationName || dropOffLocation?.locationName,
            code: pickUpLocation?.locationCode || dropOffLocation?.locationCode,
            locationAddress:
              pickUpLocation?.locationAddress ||
              dropOffLocation?.locationAddress,
            locationCity:
              pickUpLocation?.locationCity || dropOffLocation?.locationCity,
            locationNumber:
              pickUpLocation?.locationNumber || dropOffLocation?.locationNumber,
            publicWebDescription:
              pickUpLocation?.publicWebDescription ||
              dropOffLocation?.publicWebDescription,
            publicWebDescriptionEN:
              pickUpLocation?.publicWebDescriptionEN ||
              dropOffLocation?.publicWebDescriptionEN,
            zipCode: pickUpLocation?.zipCode || dropOffLocation?.zipCode,
          };
          currentBookingDetails.dropOffLocationDetails = {
            reservationSourcePayLater:
              searchParams.get("reservationSourcePayLater") === "true",
            reservationSourcePayNow:
              searchParams.get("reservationSourcePayLater") === "true",
            locationAddress: dropOffLocation?.locationAddress,
            locationCity: dropOffLocation?.locationCity,
            locationNumber: dropOffLocation?.locationNumber,
            publicWebDescription: dropOffLocation?.publicWebDescription,
            publicWebDescriptionEN: dropOffLocation?.publicWebDescriptionEN,
            zipCode: dropOffLocation?.zipCode,
            name: dropOffLocation?.locationName,
            code: dropOffLocation?.locationCode,
          };
          currentBookingDetails.dropAtSameLocation =
            pickUpLocation.locationCode == dropOffLocation.locationCode;
          return BookingApi.getAllReservationSources();
        })
        .then((reservationSourcesResponse) => {
          let reservationSource;
          if (scriptChannel && scriptChannel !== "") {
            reservationSource = {
              payLater: false,
              payNow: false,
              "Ragione Sociale textRS": "",
              Codice: scriptChannel,
            };
          } else {
            reservationSource = reservationSourcesResponse?.find((source) => {
              return source.Codice == t.reservationSource;
            });
            if (!reservationSource) {
              navigation("/");
              return Promise.reject(new Error("Invalid reservation source"));
            }
          }
          currentBookingDetails.reservationSource = {
            reservationSourcePayLater: reservationSource.payLater ?? false,
            reservationSourcePayNow: reservationSource.payNow ?? false,
            locationAddress: "",
            locationCity: "",
            locationNumber: "",
            publicWebDescription: "",
            publicWebDescriptionEN: "",
            zipCode: "",
            name: reservationSource["Ragione Sociale textRS"],
            code: reservationSource.Codice,
          };
          // Use URL+session delivery data only when delivery is actually
          // selected; otherwise fall back to Redux so non-URL entry paths
          // (deep links, reserved-area, back/forward) don't lose the surcharge.
          const deliverySource =
            deliveryFromUrl.homeDeliverySelected ||
            deliveryFromUrl.homeReturnSelected
              ? deliveryFromUrl
              : reduxBookingDetails;
          const bookingDetailsWithDelivery = {
            ...currentBookingDetails,
            homeDeliverySelected: deliverySource.homeDeliverySelected,
            homeReturnSelected: deliverySource.homeReturnSelected,
            homeDeliveryAddress: deliverySource.homeDeliveryAddress,
            homeReturnAddress: deliverySource.homeReturnAddress,
            deliveryCoordinates: deliverySource.deliveryCoordinates,
            returnCoordinates: deliverySource.returnCoordinates,
            homeDeliveryAddressKm: deliverySource.homeDeliveryAddressKm,
            homeReturnAddressKm: deliverySource.homeReturnAddressKm,
          };
          dispatch(
            BOOKING_ACTIONS.setBookingInformation(bookingDetailsWithDelivery)
          );
          return BookingApi.getReservationVehicles(bookingDetailsWithDelivery);
        })
        .then((vehiclesResponse) => {
          //the below code is written to find if the vehicle shared via url is available or not
          const urlParams = new URLSearchParams(window.location.search);
          const vehicleInternationalCode = urlParams.get(
            "vehicleInternationalCode"
          );
          const vehicles = vehiclesResponse?.data?.data?.Vehicles || [];
          const matchedVehicle = vehicles.find(
            (v) => v?.Vehicle?.Code === vehicleInternationalCode
          );

          if (!matchedVehicle) {
            setIsVehicleUnavQuot(true); // <-- your variable
          }
          dispatch(
            BOOKING_ACTIONS.setSessionExpiryTime(
              addMinutes(new Date(), TimeInMinutes).getTime()
            )
          );
          setErrorMsgNoVehicle(
            vehiclesResponse.data?.data?.errors?.Error?.ShortText
          );
          setAllVehicles([...(vehiclesResponse.data?.data?.Vehicles || [])]);
        })
        .catch((error) => {
          console.error(error);
          navigation("/");
        })
        .finally(() => {
          setActiveStep(0);
          setPushState(0);
          setIsLoading(false);
        });
    }
  }, [searchParams]);

  const scrollToTop = () => {
    window.parent.postMessage(
      {
        event_id: "scrollToTop",
      },
      "*"
    );
  };
  const isSmallScreen = window.innerWidth <= 768;
  const isMobileView = window.innerWidth < 500;
  const isIpadMiniScreen = window.innerWidth >= 500 && window.innerWidth <= 768;
  const isDesktopScreen = window.innerWidth >= 1300;
  const istabletScreen = window.innerWidth > 768 && window.innerWidth < 1300;
  const InTabletScreenFilter =
    window.innerWidth >= 768 && window.innerWidth <= 1000;
  const extraInsIFrameMobile =
    Number(appSettings?.iframeHeightThirdPageForMobile) || 30;
  const extraInsIFrameDesktop =
    Number(appSettings?.iframeHeightForThirdPage) || 80;
  const insuranceRef = useRef(null);
  const [insuranceDivHeight, SetInsuranceDivHeight] = useState<number>(0);
  const [extrasDivHeight, SetExtrasDivHeight] = useState<number>(0);
  const [isVehicleUnavQuot, setIsVehicleUnavQuot] = useState(false); // is vehicle not available in quotation api
  const [isFindMoreTriggeredInInsComp, setIsFindMoreTriggeredInInsComp] =
    useState(false);
  const isGretaBookingNewUi = appSettings?.isGretaBookingNewUi;

  useEffect(() => {
    if (insuranceRef.current) {
      const insDivHeight = insuranceRef.current.offsetHeight;
      SetInsuranceDivHeight(insDivHeight);
    }
    if (selectVehicleExtrasRef.current) {
      const extrasHeight = selectVehicleExtrasRef.current.offsetHeight;
      SetExtrasDivHeight(extrasHeight);
    }
  });
  // in all rendering i m setting the insurance and extra height. I am also updating the height when we click the finoutmore button on ins card
  useEffect(() => {
    if (insuranceRef.current) {
      const insDivHeight = insuranceRef.current.offsetHeight;
      SetInsuranceDivHeight(insDivHeight);
    }
  }, [isFindMoreTriggeredInInsComp]);
  useEffect(() => {
    if (
      (activeStep == 1 || (activeStep == 2 && showUpgradeProposals)) &&
      appSettings?.showInIframe
    ) {
      requestParentResize();
    }
  });
  function requestParentResize() {
    let iframeHeight;
    if (isSmallScreen) {
      const recapHeight = isExpanded1
        ? 377
        : isExpanded2
        ? 361
        : isExpanded3
        ? 371
        : isExpanded4
        ? 349
        : 221;
      iframeHeight =
        insuranceDivHeight +
        extrasDivHeight +
        recapHeight +
        230 +
        extraInsIFrameMobile; //131 is extra spaces between button and extraInsIFrameMobile is extra space we taken
    } else {
      iframeHeight =
        insuranceDivHeight +
        extrasDivHeight +
        112 +
        250 +
        extraInsIFrameDesktop; //112px is 7rem space between cards, 184px is recap height + extraInsIFrameDesktop(some extra margin)
    }

    window.parent.postMessage(
      {
        event_id: "iframeHeight",
        data: {
          iframeHeight: iframeHeight,
        },
      },
      "*"
    );
  }

  useEffect(() => {
    if (
      (activeStep == 1 || (activeStep == 2 && showUpgradeProposals)) &&
      appSettings?.showInIframe
    ) {
      requestParentResize();
    }
    if (activeStep == 2) {
      window.scrollTo(0, 50);
    }
  }, [activeStep, showUpgradeProposals]);
  const [open, setOpen] = useState(false);
  const handleDrawerToggle = () => {
    setOpen(!open);
  };
  const [findInsuranceBody, setFindInsuranceBody] = useState({});
  const findTextForInsuranceBody = (feature, vehicleName) => {
    if (!findInsuranceBody[feature]) {
      const textVal = data.find(
        (item) =>
          item.key === "insurance:" + feature.toUpperCase() + ":" + vehicleName
      );
      setFindInsuranceBody((prevData) => ({
        ...prevData,
        [feature]: textVal ? textVal?.body : "",
      }));
    }
  };
  const isFilterEnable =
    (enableFilterByFuel && filterfuelTypeData.length > 0) ||
    (enableFilterBySeat && filterseatData.length > 0) ||
    (enableFilterByTransmission && filtertransmissionData.length > 0) ||
    enableFilterByPrice ||
    enableFilterByPremium;
  /*------------------------------Handle card in mobile and table view----------------------------------------------------*/
  const [isExpanded1, setIsExpanded1] = useState(true);
  const [isExpanded2, setIsExpanded2] = useState(false);
  const [isExpanded3, setIsExpanded3] = useState(false);
  const [isExpanded4, setIsExpanded4] = useState(false);
  const defaultCurrency = appSettings?.defaultCurrency;
  const handleForwardClick = () => {
    if (!hideInsExtrasThirdTab) {
      setVisibleCards(visibleCards.map((index) => index + 2));
    } else {
      setVisibleCards([1, 3]);
    }
  };
  const handleBackwardClick = () => {
    if (!hideInsExtrasThirdTab) {
      setVisibleCards(visibleCards.map((index) => index - 2));
    } else {
      setVisibleCards([0, 1]);
    }
  };

  const handleStepTwoActions = async (vehicleDetails) => {
    if (vehicleDetails) {
      const token = ++stepTwoNavTokenRef.current;
      const totalPrice =
        searchParams.get("reservationSourcePayLater") === "true" ||
        (userId != null && userId != "null" && userId != "")
          ? parseFloat(
              (vehicleDetails?.Reference?.ID &&
              vehicleDetails?.Reference?.ID > 0
                ? (
                    vehicleDetails?.Reference.originalAmount -
                    vehicleDetails?.Reference.ID
                  ).toString()
                : vehicleDetails?.TotalCharge?.RateTotalAmount.toString()) ||
                "0"
            )
          : parseFloat(
              (vehicleDetails?.Reference?.ID &&
              vehicleDetails?.Reference?.ID > 0
                ? (
                    vehicleDetails?.Reference?.originalAmount -
                    vehicleDetails?.Reference.ID
                  ).toString()
                : vehicleDetails?.TotalCharge?.RateTotalAmount.toString()) ||
                "0"
            ) * discount;

      setSelectedPrice(totalPrice);
      dispatch(
        BOOKING_ACTIONS.setSelectedVehicleDetails({
          paymentDetails: {
            addedExtraOptions: selectedFields?.options,
            isDiscounted:
              searchParams.get("reservationSourcePayLater") === "true" ||
              (userId != null && userId != "null" && userId != "")
                ? 0
                : (parseFloat(
                    (selectedVehicle?.Reference?.ID &&
                    selectedVehicle?.Reference?.ID > 0
                      ? (
                          selectedVehicle?.Reference?.originalAmount -
                          selectedVehicle?.Reference.ID
                        ).toString()
                      : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) ||
                      "0"
                  ) *
                    parseFloat(selectedVehicle?.Vehicle?.payNowDis || "0")) /
                    100 || 0,
            price: totalPrice,
            selectedInsurance: selectedFields?.insurance,
            isPayLaterFee: false,
            payLaterFeeAmt: "",
            type: "",
            extrasPrice: extrasPrice,
            extrasIncluded: true,
            ...(cardPaymentChoice && {
              type: cardPaymentChoice.type,
              price: cardPaymentChoice.price,
              isDiscounted: cardPaymentChoice.isDiscounted,
              surchargeAmt: cardPaymentChoice.surchargeAmt,
            }),
          },
          vehicleDetails: vehicleDetails,
        })
      );

      let shouldShowUpgradeProposals = false;
      if (appSettings?.enableUpgradeProposals) {
        setSelectedCancellationUpgrade(false);
        setSelectedVehicleUpgradeChoice(false);
        setSelectedProtectionUpgrade(false);
        originalInsuranceBeforeUpgradeRef.current = selectedFields.insurance;
        upgradeStepCompletedRef.current = false;
        setIsResolvingUpgradeOffers(true);
        try {
          const reservationSourceCode =
            searchParams.get("reservationSource") || "WEB001";
          const vehicleGroupCode =
            vehicleDetails?.Vehicle?.groupPic?.nationalCode || "";
          const selectedInsuranceId =
            selectedFields.insurance?.insuranceId || "";
          const fetchKey = `${reservationSourceCode}|${vehicleGroupCode}|${selectedInsuranceId}`;
          if (upgradeProposalsFetchRef.current.fetchedFor === fetchKey) {
            shouldShowUpgradeProposals = await waitForUpgradeProposals();
          } else {
            await startUpgradeProposalsFetch(fetchKey, {
              reservationSourceCode,
              vehicleGroupCode,
              selectedInsuranceId: selectedInsuranceId || undefined,
            });
            shouldShowUpgradeProposals = upgradeProposalsFetchRef.current.ready;
          }
        } finally {
          setIsResolvingUpgradeOffers(false);
        }
      }

      if (!isMountedRef.current || stepTwoNavTokenRef.current !== token) return;
      setActiveStep(2);
      setPushState(2);
      setShowUpgradeProposals(shouldShowUpgradeProposals);
    }
  };
  const [allPayNowMethods, setAllPayNowMethods] = useState<
    Partial<AllPaymentMethodsResponse>
  >({});
  const [allPayLaterMethods, setAllPayLaterMethods] = useState<
    Partial<AllPaymentMethodsResponse>
  >({});
  const affiliateId = searchParams.get("affiliateId") || "";
  const [showScalapayWidgetPayNow, setShowScalapayWidgetPayNow] =
    useState<boolean>(false);
  const [showScalapayWidgetPayLater, setShowScalapayWidgetPayLater] =
    useState<boolean>(false);
  const [scalapayMerchantToken, setScalapayMerchantToken] =
    useState<string>("");
  const [scalapayEnvVal, setScalapayEnvVal] = useState<string>("");
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const e = await BookingApi.getAllNewPaymentMethods(affiliateId);

        if (e?.payNow && Object.keys(e.payNow).length > 0) {
          setAllPayNowMethods(e.payNow);
          if (Object.hasOwn(e.payNow, "Scalapay")) {
            setShowScalapayWidgetPayNow(true);
            setScalapayMerchantToken(
              e.payNow["Scalapay"]?.merchant_token.toString()
            );
            setScalapayEnvVal(e.payNow["Scalapay"]?.environment || "");
          }
        } else if (e?.allPayments) {
          setAllPayNowMethods(e.allPayments);
        } else {
          setAllPayNowMethods({});
        }

        if (e?.payLater && Object.keys(e.payLater).length > 0) {
          setAllPayLaterMethods(e.payLater);
          if (Object.hasOwn(e.payLater, "Scalapay")) {
            setShowScalapayWidgetPayLater(true);
            setScalapayMerchantToken(
              e.payLater["Scalapay"]?.merchant_token.toString()
            );
            setScalapayEnvVal(e.payLater["Scalapay"]?.environment || "");
          }
        } else if (e?.allPayments) {
          setAllPayLaterMethods(e.allPayments);
        } else {
          setAllPayLaterMethods({});
        }
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    };
    fetchPaymentMethods();
  }, [affiliateId]);

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const toggleExpand1 = () => {
    setIsExpanded2(false);
    setIsExpanded3(false);
    setIsExpanded4(false);
    setIsExpanded1(!isExpanded1);
  };
  const toggleExpand2 = () => {
    if (
      activeStep == 1 ||
      activeStep == 2 ||
      (isEdit == true && activeStep == 0)
    ) {
      setIsExpanded1(false);
      setIsExpanded3(false);
      setIsExpanded4(false);
      setIsExpanded2(!isExpanded2);
    }
  };
  const toggleExpand3 = () => {
    if (
      activeStep == 1 &&
      selectedFields.insurance == null &&
      Object.keys(selectedFields.options).length == 0
    ) {
      setIsExpanded1(false);
      setIsExpanded2(false);
      setIsExpanded4(false);
      setIsExpanded3(false);
      if (includedIns.length) {
        setIsExpanded3(!isExpanded3);
      }
    } else if (activeStep == 1 || activeStep == 2) {
      setIsExpanded1(false);
      setIsExpanded2(false);
      setIsExpanded4(false);
      setIsExpanded3(!isExpanded3);
    }
  };
  const toggleExpand4 = () => {
    if (activeStep == 2) {
      setIsExpanded1(false);
      setIsExpanded3(false);
      setIsExpanded2(false);
      setIsExpanded4(!isExpanded4);
    }
  };
  useEffect(() => {
    if (activeStep == 1 && !hideInsExtrasThirdTab && !isEdit) {
      handleForwardClick();
    }
    if (activeStep == 2 && hideInsExtrasThirdTab) {
      handleForwardClick();
    }
    if (
      activeStep == 1 &&
      selectedFields.insurance == null &&
      Object.keys(selectedFields.options).length == 0
    ) {
      setIsExpanded1(false);
      setIsExpanded2(true);
      setIsExpanded3(false);
      setIsExpanded4(false);
    }
    if (activeStep == 2 && hideInsExtrasThirdTab) {
      setIsExpanded1(false);
      setIsExpanded2(false);
      setIsExpanded3(false);
      setIsExpanded4(true);
      handleForwardClick();
    }
    if (activeStep == 2) {
      setIsExpanded2(false);
      setIsExpanded3(false);
      setIsExpanded4(true);
    }
  }, [activeStep]);
  useEffect(() => {
    if (
      Object.keys(selectedFields.options).length > 0 ||
      selectedFields.insurance != null ||
      includedIns.length
    ) {
      setIsExpanded3(true);
      setIsExpanded1(false);
      setIsExpanded2(false);
      setIsExpanded4(false);
    }
  }, [selectedFields]);
  const [visibleCards, setVisibleCards] = useState([0, 1]);
  const getClassName = () => {
    let className = "flex flex-col gap-4 grow box-border w-full ";
    if (isSmallScreen) {
      if (isGretaBookingNewUi) {
        className += "mt-[2%]";
        // Every branch below this one is legacy-UI-only: since this branch
        // is unconditional, none of them can ever fire for a Greta tenant.
        // They stay in place only for the non-Greta layout, which still
        // relies on this percentage-of-width margin to clear its own
        // filter/expand panel (see the isFilterEnable block below).
      } else if (!isFilterEnable && isExpanded1 && isIpadMiniScreen) {
        className += "mt-[60%]";
      } else if (!isFilterEnable && isExpanded1) {
        className += "mt-[100%]";
      } else if (
        !isExpanded2 &&
        !isExpanded1 &&
        !isExpanded3 &&
        !isExpanded4 &&
        !isFilterEnable
      ) {
        className += "mt-[65%]";
      } else if (
        !isFilterEnable &&
        !isExpanded1 &&
        isIpadMiniScreen &&
        activeStep == 0
      ) {
        className += "mt-[60%]";
      } else if (!isFilterEnable && !isExpanded1) {
        className += isEdit ? "mt-[100%]" : "mt-[60%]";
      }
    } else {
      className += "mt-[0%]";
    }

    return className;
  };

  const getMarginTop = () => {
    if (
      isExpanded2 == false &&
      isExpanded1 == false &&
      isExpanded3 == false &&
      isMobileView
    ) {
      return "50%";
    } else if (
      isIpadMiniScreen &&
      isExpanded2 == false &&
      isExpanded1 == false &&
      isExpanded3 == false
    ) {
      return "30%";
    } else if (isIpadMiniScreen) {
      return "50%";
    } else if (isMobileView) {
      if (isGretaBookingNewUi) {
        return "0";
      } else {
        return "95%";
      }
    }
  };
  const payMarginTop = () => {
    if (
      !isExpanded2 &&
      !isExpanded1 &&
      !isExpanded3 &&
      !isExpanded4 &&
      isMobileView
    ) {
      return "50%";
    } else if (
      !isExpanded2 &&
      !isExpanded1 &&
      !isExpanded3 &&
      !isExpanded4 &&
      isIpadMiniScreen
    ) {
      return "30%";
    } else if (isIpadMiniScreen) {
      return "60%";
    } else if (isMobileView) {
      if (isGretaBookingNewUi) {
        return "0";
      } else {
        return "85%";
      }
    }
  };
  const [includedIns, setIncludedIns] = useState<any[]>([]);

  const isCoverageIncluded = (e?: {
    optionalsCode?: string | null;
    Amount?: number;
  }) =>
    e?.Amount === 0 ||
    (selectedVehicle?.optionals || []).some(
      (opt) =>
        opt?.Equipment?.Code === e?.optionalsCode &&
        opt?.Charge?.IncludedInRate === true
    );

  // BOOKING-panel no-coverage warning: the most complete/highest-priced
  // paid coverage still available, or null if none exists.
  const getBestAvailableCoverage = () => {
    const paidCoverages = (insuranceList["Booking Insurance"] || []).filter(
      (e) =>
        !isCoverageIncluded(e) && parseFloat((e.Amount ?? 0).toString()) > 0
    );
    if (paidCoverages.length === 0) return null;
    return paidCoverages.reduce((best, current) =>
      parseFloat(current.Amount.toString()) > parseFloat(best.Amount.toString())
        ? current
        : best
    );
  };

  const shouldShowNoCoverageWarning = () =>
    !!appSettings?.enableCoverageWarningPopup &&
    !selectedFields?.insurance &&
    getBestAvailableCoverage() != null;

  // Fires once totalPrice/insurancePrice have recalculated after
  // "Add {coverageName} and continue" sets the newly-picked coverage into
  // selectedFields -- reuses the existing price-recompute effect instead of
  // re-deriving totals here, so it can't drift from the real numbers.
  useEffect(() => {
    if (!autoProceedAfterAddRef.current) return;
    autoProceedAfterAddRef.current = false;
    const type = pendingProceedTypeRef.current;
    scrollToTop();
    window.scrollTo(0, 0);
    if (selectedVehicle) {
      dispatch(
        BOOKING_ACTIONS.setSelectedVehicleDetails({
          paymentDetails: {
            addedExtraOptions: selectedFields?.options,
            isDiscounted:
              searchParams.get("reservationSourcePayLater") === "true" ||
              (userId != null && userId != "null" && userId != "")
                ? 0
                : (parseFloat(
                    (selectedVehicle?.Reference?.ID &&
                    selectedVehicle?.Reference?.ID > 0
                      ? (
                          selectedVehicle?.Reference?.originalAmount -
                          selectedVehicle?.Reference.ID
                        ).toString()
                      : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) ||
                      "0"
                  ) *
                    parseFloat(selectedVehicle?.Vehicle?.payNowDis)) /
                    100 || 0,
            price: totalPrice,
            selectedInsurance: selectedFields?.insurance,
            type,
            isPayLaterFee: false,
            payLaterFeeAmt: "",
            extrasPrice: extrasPrice,
            extrasIncluded: true,
            ...(cardPaymentChoice && {
              type: cardPaymentChoice.type,
              price: cardPaymentChoice.price,
              isDiscounted: cardPaymentChoice.isDiscounted,
              surchargeAmt: cardPaymentChoice.surchargeAmt,
            }),
          },
          vehicleDetails: selectedVehicle,
        })
      );
      goToStepTwo();
    }
    setSelectedPrice(totalPrice);
  }, [insurancePrice, totalPrice]);

  useEffect(() => {
    if (insuranceList["Booking Insurance"] != null) {
      const includedInsurance = insuranceList["Booking Insurance"].filter((e) =>
        isCoverageIncluded(e)
      );
      setIncludedIns(includedInsurance);
    }
    if (activeStep === 0 && includedIns.length) {
      setIncludedIns([]);
    }
    if (activeStep === 0 && selectedFields != null) {
      setSelectedFields({
        options: {},
        insurance: undefined,
      });
    }
  }, [insuranceList["Booking Insurance"], activeStep, selectedVehicle]);

  /*----------------------------------------------------------------------------------------------------*/
  const showByPaymentType =
    paymentType === "payNow"
      ? showScalapayWidgetPayNow === true
      : paymentType === "payLater"
      ? showScalapayWidgetPayLater === true
      : false;

  const showForScriptChannel =
    (scriptChannel &&
      ((showScalapayWidgetPayNow && paymentType == "payNow") ||
        (showScalapayWidgetPayLater && paymentType == "payLater"))) ||
    (reservationSourcePayNow == "true" && showScalapayWidgetPayNow) ||
    (reservationSourcePayLater == "true" && showScalapayWidgetPayLater);

  const showForNonScriptChannel =
    scriptChannel == "" &&
    reservationSourcePayNow === "false" &&
    reservationSourcePayLater === "false" &&
    (showScalapayWidgetPayNow === true || showScalapayWidgetPayLater === true);
  const isReservedAreaUser = !!localStorage.getItem("userID");

  const shouldShowScalapayWidget =
    !isReservedAreaUser &&
    showByPaymentType &&
    (showForScriptChannel || showForNonScriptChannel);

  const cards = [
    {
      label: (
        <div
          className="flex flex-col p-2 grow h-full"
          style={
            appSettings?.fillColorToCard
              ? {
                  border: `2px solid ${dynamicTheme?.palette["primary"]?.main}`,
                  background: dynamicTheme?.palette["ternaryColor"]?.main,
                  borderRadius: appSettings?.recapHeaderBorderRadius
                    ? `${appSettings?.recapHeaderBorderRadius}rem`
                    : "0px",
                }
              : {
                  borderBottom: `5px solid ${dynamicTheme?.palette["ternaryColor"]?.main}`,
                  borderRadius: appSettings?.recapHeaderBorderRadius
                    ? `${appSettings?.recapHeaderBorderRadius}rem`
                    : "0px",
                  background: recapCardBackgroundColor
                    ? recapCardBackgroundColor
                    : "#ffffff",
                }
          }
        >
          <div className="mb-2">
            <div className="flex justify-between">
              <span
                className="text-xs text-white flex items-center justify-center mt-[5px]"
                style={{
                  background: dynamicTheme?.palette["primary"]?.main,
                  width: "20px",
                  height: "20px",
                  borderRadius: "10px",
                }}
              >
                1
              </span>

              <p
                className={`text-lg text-black grow ml-[10px] whitespace-nowrap`}
                style={
                  appSettings?.fillColorToCard
                    ? {
                        color:
                          dynamicTheme?.palette["ternaryColor"].contrastText,
                      }
                    : {
                        color:
                          dynamicTheme?.palette["backgroundColor"].contrastText,
                      } && { fontWeight: "normal" }
                }
              >
                {translate("Selected Details")}
              </p>
              <button
                style={{
                  color: dynamicTheme?.palette["backgroundColor"].contrastText,
                }}
                onClick={() => {
                  if (redirectUrl != null && redirectUrl != "") {
                    window.location.href = redirectUrl;
                    hideIframe();
                  } else {
                    handleEdit();
                  }
                }}
              >
                {t("Edit")}
              </button>
            </div>
            <div>
              <div className="flex flex-row text-black justify-between">
                <div className="p-2 text-left">
                  <h6
                    className="text-sm"
                    style={
                      appSettings?.fillColorToCard
                        ? {
                            color:
                              dynamicTheme?.palette["ternaryColor"]
                                .contrastText,
                          }
                        : {
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                          }
                    }
                  >
                    {pickUpLabel}
                  </h6>
                  <div
                    className="pt-3"
                    style={
                      appSettings?.fillColorToCard
                        ? {
                            color:
                              dynamicTheme?.palette["ternaryColor"]
                                .contrastText,
                          }
                        : {
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                          }
                    }
                  >
                    <span style={{ fontSize: "large", fontWeight: "bold" }}>
                      {appSettings?.showLocationNameInSummary
                        ? bookingInformation?.pickUpLocationName
                        : bookingInformation?.pickUpLocationDetails?.code}
                    </span>
                  </div>
                  <div
                    style={
                      appSettings?.fillColorToCard
                        ? {
                            color:
                              dynamicTheme?.palette["ternaryColor"]
                                .contrastText,
                          }
                        : {
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                          }
                    }
                  >
                    <p style={{ fontSize: "small" }}>
                      {t("date", {
                        date: new Date(bookingInformation.pickUpDateAndTime),
                      })}{" "}
                      {new Date(bookingInformation.pickUpDateAndTime)
                        .getHours()
                        .toString()
                        .padStart(2, "0")}
                      {":"}
                      {new Date(bookingInformation.pickUpDateAndTime)
                        .getMinutes()
                        .toString()
                        .padStart(2, "0")}
                    </p>
                  </div>
                </div>
                <div className="p-2 text-left">
                  <h6
                    className="text-sm"
                    style={
                      appSettings?.fillColorToCard
                        ? {
                            color:
                              dynamicTheme?.palette["ternaryColor"]
                                .contrastText,
                            fontWeight: "500px",
                          }
                        : {
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                            fontWeight: "500px",
                          }
                    }
                  >
                    {dropOffLabel}
                  </h6>
                  <div
                    className="pt-3"
                    style={
                      appSettings?.fillColorToCard
                        ? {
                            color:
                              dynamicTheme?.palette["ternaryColor"]
                                .contrastText,
                          }
                        : {
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                          }
                    }
                  >
                    <span style={{ fontSize: "large", fontWeight: "bold" }}>
                      {appSettings?.showLocationNameInSummary
                        ? bookingInformation.dropOffLocationName
                        : bookingInformation.dropOffLocationDetails.code}
                    </span>
                  </div>
                  <div
                    style={
                      appSettings?.fillColorToCard
                        ? {
                            color:
                              dynamicTheme?.palette["ternaryColor"]
                                .contrastText,
                          }
                        : {
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                          }
                    }
                  >
                    <p style={{ fontSize: "small" }}>
                      {t("date", {
                        date: new Date(bookingInformation.dropOffDateAndTime),
                      })}{" "}
                      {new Date(bookingInformation.dropOffDateAndTime)
                        .getHours()
                        .toString()
                        .padStart(2, "0")}
                      {":"}
                      {new Date(bookingInformation.dropOffDateAndTime)
                        .getMinutes()
                        .toString()
                        .padStart(2, "0")}
                    </p>
                  </div>
                </div>
              </div>
              {searchParams.get("agreementCoupon") && (
                <div style={{ color: "red", paddingLeft: "9px" }}>
                  <p>Codice promo: {searchParams.get("agreementCoupon")}</p>
                </div>
              )}
            </div>
            {appSettings?.showResSourceSelectionInSecondStep ? (
              <>
                <div
                  style={{
                    color: dynamicTheme?.palette["secondaryColor"]?.main,
                    paddingLeft: "9px",
                  }}
                >
                  <p>
                    {translate("Choosen Rate")}:{" "}
                    {ReservationDetails.reservationSource.name}
                  </p>
                </div>
              </>
            ) : (
              <>
                {(searchParams.get("reservationSourcePayNow") === "true" ||
                  searchParams.get("reservationSourcePayLater") === "true") &&
                  !isInvalidScriptChannelResSource && (
                    <div
                      className="flex "
                      style={
                        appSettings?.fillColorToCard
                          ? {
                              color:
                                dynamicTheme?.palette["ternaryColor"]
                                  .contrastText,
                            }
                          : {
                              color:
                                dynamicTheme?.palette["backgroundColor"]
                                  .contrastText,
                            }
                      }
                    >
                      <span style={{ color: "red", marginLeft: "10px" }}>
                        {" "}
                        {translate("Mode")}
                      </span>
                      {searchParams.get("reservationSourcePayNow") ===
                        "true" && (
                        <span style={{ color: "red", marginLeft: "10px" }}>
                          {" "}
                          {translate("Pay Now")}
                        </span>
                      )}
                      {searchParams.get("reservationSourcePayLater") ===
                        "true" && (
                        <span style={{ color: "red", marginLeft: "10px" }}>
                          {" "}
                          {translate("Pay Later")}
                        </span>
                      )}
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      label: (
        <div
          className="flex flex-col p-2 grow h-full"
          style={
            appSettings?.fillColorToCard
              ? {
                  border: `2px solid ${dynamicTheme?.palette["primary"].main}`,
                  borderRadius: appSettings?.recapHeaderBorderRadius
                    ? `${appSettings?.recapHeaderBorderRadius}rem`
                    : "0px",
                  background:
                    activeStep > 0
                      ? dynamicTheme?.palette["ternaryColor"].main
                      : "inherit",
                }
              : {
                  borderLeft: `1px solid #cccccc`,
                  borderRadius: appSettings?.recapHeaderBorderRadius
                    ? `${appSettings?.recapHeaderBorderRadius}rem`
                    : "0px",
                  borderBottom:
                    activeStep > 0
                      ? `4px solid ${dynamicTheme?.palette["ternaryColor"].main}`
                      : `4px solid #cccccc`,
                  borderTop: "none",
                  background: recapCardBackgroundColor
                    ? recapCardBackgroundColor
                    : "#ffffff",
                }
          }
        >
          <div className="flex justify-between">
            <span
              className="text-xs text-white flex items-center justify-center mt-[5px]"
              style={{
                background: dynamicTheme?.palette["primary"]?.main,
                width: "20px",
                height: "20px",
                borderRadius: "10px",
              }}
            >
              2
            </span>
            <p
              className={`text-lg text-black grow ml-[10px]`}
              style={
                {
                  color:
                    activeStep > 0 && appSettings?.fillColorToCard
                      ? dynamicTheme?.palette["ternaryColor"].contrastText
                      : dynamicTheme?.palette["backgroundColor"].contrastText,
                } && activeStep == 0
                  ? { fontWeight: "bold" }
                  : { fontWeight: "normal" }
              }
            >
              {t("Car Selection")}
            </p>
            <button
              style={{
                color: dynamicTheme?.palette["backgroundColor"].contrastText,
              }}
              onClick={() => {
                if (activeStep > 0) {
                  setActiveStep(0);
                  if (activeStep == 2) {
                    window.history.pushState({ step: 2 }, "Step 2", "");
                  }
                  setIsEdit(false);
                  setIsNotClickedEdit(false);
                  setIsLoading(false);
                  setSelectedVehicle(undefined);
                }
              }}
            >
              {t("Edit")}
            </button>
          </div>
          {selectedVehicle ? (
            <div className="flex flex-row text-black justify-between">
              <div className="p-2 text-left">
                <h4
                  className="text-md font-bold"
                  style={{
                    color:
                      activeStep > 0 && appSettings?.fillColorToCard
                        ? dynamicTheme?.palette["ternaryColor"].contrastText
                        : dynamicTheme?.palette["backgroundColor"].contrastText,
                  }}
                >
                  {t("Vehicle")}
                </h4>
                <div
                  className="pt-1"
                  style={{
                    fontSize: "small",
                    color:
                      activeStep > 0 && appSettings?.fillColorToCard
                        ? dynamicTheme?.palette["ternaryColor"].contrastText
                        : dynamicTheme?.palette["backgroundColor"].contrastText,
                  }}
                >
                  {/* showVehicleGroupDescInRecap is used to show the vehicle description in recap bar */}
                  {appSettings?.showVehicleGroupDescInRecap ? (
                    <>
                      <span>
                        Gruppo {selectedVehicle.Vehicle?.VendorCarType}
                      </span>
                      <br />
                      <span>
                        {selectedVehicle.Vehicle?.groupWebDescription}
                      </span>
                    </>
                  ) : (
                    <>
                      {selectedVehicle.Vehicle?.VehMakeModel?.Name}
                      <span>{selectedVehicle.Vehicle?.vehicelExtraImage}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="p-2 text-left">
                <h4 className="text-md text-[primary] font-bold"></h4>
                <div className="pt-1">
                  <span className="pt-1 pb-1 mr-1">
                    <img
                      src={
                        (selectedVehicle.Vehicle?.vehicleGroupPic ||
                          selectedVehicle.Vehicle?.vehicelExtraImage) as string
                      }
                      className="md:min-w-full aspect-video object-contain h-24"
                    />
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      ),
    },
    !hideInsExtrasThirdTab && {
      disabled: !selectedVehicle,
      label: (
        <>
          <section
            className="flex flex-col h-full w-full grow p-2 grow h-full"
            style={
              appSettings?.fillColorToCard
                ? {
                    border: `2px solid ${dynamicTheme?.palette["primary"].main}`,
                    borderRadius: appSettings?.recapHeaderBorderRadius
                      ? `${appSettings?.recapHeaderBorderRadius}rem`
                      : "0px",
                    background:
                      activeStep > 1
                        ? dynamicTheme?.palette["ternaryColor"].main
                        : "inherit",
                  }
                : {
                    borderLeft: `1px solid #cccccc`,
                    borderRadius: appSettings?.recapHeaderBorderRadius
                      ? `${appSettings?.recapHeaderBorderRadius}rem`
                      : "0px",
                    borderBottom:
                      activeStep > 1
                        ? `4px solid ${dynamicTheme?.palette["ternaryColor"].main}`
                        : `4px solid #cccccc`,
                    borderTop: "none",
                    background: recapCardBackgroundColor
                      ? recapCardBackgroundColor
                      : "#ffffff",
                  }
            }
          >
            <div className="flex flex-row">
              <span
                className="text-xs text-white flex items-center justify-center mt-[5px]"
                style={{
                  background: dynamicTheme?.palette["primary"]?.main,
                  width: "20px",
                  height: "20px",
                  borderRadius: "10px",
                }}
              >
                3
              </span>
              <p
                className="text-lg text-black grow ml-[10px]"
                style={
                  {
                    color:
                      activeStep > 1 && appSettings?.fillColorToCard
                        ? dynamicTheme?.palette["ternaryColor"].contrastText
                        : dynamicTheme?.palette["backgroundColor"].contrastText,
                  } && activeStep == 1
                    ? { fontWeight: "bold" }
                    : { fontWeight: "normal" }
                }
              >
                Extra
              </p>
              <button
                className="h-fit"
                style={{
                  color: dynamicTheme?.palette["backgroundColor"].contrastText,
                }}
                onClick={() => {
                  if (activeStep > 1) {
                    setActiveStep(1);
                    setPushState(1);
                    setIsLoading(false);
                    setIsNotClickedEdit(false);
                    setShowUpgradeProposals(false);
                    if (istabletScreen) {
                      setIsEdit(true);
                    }
                  }
                }}
              >
                {t("Edit")}
              </button>
            </div>
            <div
              className={`flex flex-row text-black whitespace-nowrap ${
                Object.keys(selectedFields.options).length > 0 &&
                selectedFields.insurance &&
                isDesktopScreen
                  ? "justify-around space-x-6"
                  : ""
              }`}
              style={{
                width: "100%",
              }}
            >
              {/* Left Section: Coperture Include and Insurance Added */}
              {includedIns.length > 0 ||
              selectedFields.insurance ||
              Object.values(selectedFields.options).length > 0 ? (
                <div
                  className="p-1 text-left grow"
                  style={{
                    width: "50%",
                  }}
                >
                  {includedIns.length > 0 && (
                    <div
                      style={{
                        padding: "0.25rem",

                        lineHeight: "initial",
                      }}
                    >
                      <h4
                        className="text-md font-bold"
                        style={{
                          color:
                            activeStep > 1 && appSettings?.fillColorToCard
                              ? dynamicTheme?.palette["ternaryColor"]
                                  .contrastText
                              : dynamicTheme?.palette["backgroundColor"]
                                  .contrastText,
                          fontSize: "12px",
                        }}
                      >
                        {translate("Included insurance")}
                      </h4>
                      <span
                        className="pt-1 pb-1"
                        style={{ fontSize: "10px", color: "black" }}
                      >
                        {includedIns.map((ins) => ins?.Description).join(", ")}
                      </span>
                    </div>
                  )}
                  {selectedFields.insurance && (
                    <div
                      style={{
                        padding: "0.25rem",
                        lineHeight: "initial",
                      }}
                    >
                      <h4
                        className="text-md font-bold"
                        style={{
                          color:
                            activeStep > 1 && appSettings?.fillColorToCard
                              ? dynamicTheme?.palette["ternaryColor"]
                                  .contrastText
                              : dynamicTheme?.palette["backgroundColor"]
                                  .contrastText,
                          fontSize: "12px",
                        }}
                      >
                        {t("Insurance Added")}
                      </h4>
                      <div
                        className="pt-1 whitespace-nowrap flex-row"
                        style={{
                          gap: "5px",
                          color:
                            activeStep > 1 && appSettings?.fillColorToCard
                              ? dynamicTheme?.palette["ternaryColor"]
                                  .contrastText
                              : dynamicTheme?.palette["backgroundColor"]
                                  .contrastText,
                          fontSize: "10px",
                        }}
                      >
                        <span className="pt-1 pb-1">
                          {selectedFields.insurance?.details}
                        </span>
                        <span>{selectedFields.insurance?.name}</span>
                        <span
                          className="whitespace-nowrap"
                          style={{
                            fontWeight: "bold",
                            paddingLeft: "10px",
                          }}
                        >
                          {defaultCurrency || "€"}
                          {selectedFields.insurance?.price}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <span></span>
              )}

              {/* Right Section: Added Extras */}
              {Object.keys(selectedFields.options).length > 0 && (
                <div className="p-1 text-left">
                  <h4
                    className="text-md font-bold"
                    style={{
                      color:
                        activeStep > 1 && appSettings?.fillColorToCard
                          ? dynamicTheme?.palette["ternaryColor"].contrastText
                          : dynamicTheme?.palette["backgroundColor"]
                              .contrastText,
                      fontSize: "12px",
                    }}
                  >
                    {t("Added Extras")}
                  </h4>
                  <div
                    className="pt-1"
                    style={{
                      color:
                        activeStep > 1 && appSettings?.fillColorToCard
                          ? dynamicTheme?.palette["ternaryColor"].contrastText
                          : dynamicTheme?.palette["backgroundColor"]
                              .contrastText,
                      fontSize: "10px",
                    }}
                  >
                    {Object.entries(selectedFields.options).map(
                      ([key, value]) => {
                        const hasName = !!value?.name;
                        const totalAmount = value?.price * value?.quantity || 0;

                        // Skip rendering if name exists but amount is 0
                        if (hasName && totalAmount === 0) {
                          return null;
                        }

                        // Only show text if doNotShowOptionalPrice key is present
                        const showText = Object.prototype.hasOwnProperty.call(
                          value,
                          "doNotShowOptionalPrice"
                        );
                        const text = showText
                          ? appSettings?.showDescFromBookingText
                            ? data.find((item) => item.key === value?.name)
                                ?.description || value?.name
                            : appSettings?.showOptionalDescription
                            ? value?.details
                            : value?.name
                          : "";
                        // If showText is true but text is falsy, skip the item
                        if (showText && !text) {
                          return null;
                        }
                        return (
                          <div key={key} className="flex flex-row">
                            {showText && (
                              <span className="marquee fixed-width-container">
                                {text.length < 20 ? (
                                  <span className="flex flex-row fixed-width-text">
                                    {text} {"   "}
                                  </span>
                                ) : (
                                  <span className="marquee-container">
                                    <div className="marquee-content">
                                      <span className="marquee-item">
                                        {text} {"   "}
                                      </span>
                                      <span className="marquee-item">
                                        {text} {"   "}
                                      </span>
                                    </div>
                                  </span>
                                )}
                              </span>
                            )}
                            <span
                              className="whitespace-nowrap flex flex-row font-bold ml-2"
                              style={{ fontWeight: "bold" }}
                            >
                              {showText && (
                                <>
                                  {defaultCurrency}
                                  {value?.price
                                    ? "€" +
                                      (value?.price * value?.quantity).toFixed(
                                        2
                                      )
                                    : ""}
                                </>
                              )}
                            </span>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        </>
      ),
    },

    {
      label: (
        <section
          className="flex flex-col h-full w-full grow p-2 grow h-full"
          style={
            appSettings?.fillColorToCard
              ? {
                  border: `2px solid ${dynamicTheme?.palette["primary"]?.main}`,
                  background: "none",
                  borderRadius: appSettings?.recapHeaderBorderRadius
                    ? `${appSettings?.recapHeaderBorderRadius}rem`
                    : "0px",
                }
              : {
                  borderLeft: `1px solid #cccccc`,
                  borderBottom: `4px solid #cccccc`,
                  borderTop: "none",
                  borderRadius: appSettings?.recapHeaderBorderRadius
                    ? `${appSettings?.recapHeaderBorderRadius}rem`
                    : "0px",
                  background: recapCardBackgroundColor
                    ? recapCardBackgroundColor
                    : "#ffffff",
                }
          }
        >
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row items-center">
              <span
                className="text-xs text-white flex items-center justify-center mt-[5px]"
                style={{
                  background: dynamicTheme?.palette["primary"]?.main,
                  width: "20px",
                  height: "20px",
                  borderRadius: "10px",
                }}
              >
                {!hideInsExtrasThirdTab ? "4" : "3"}
              </span>

              <p
                className="text-lg text-black ml-[10px]"
                style={
                  {
                    color:
                      activeStep > 2
                        ? dynamicTheme?.palette["backgroundColor"]?.contrastText
                        : dynamicTheme?.palette["backgroundColor"]
                            ?.contrastText,
                  } && activeStep == 2
                    ? { fontWeight: "bold" }
                    : { fontWeight: "normal" }
                }
              >
                {translate("Total Rental")}
              </p>
            </div>
            {activeStep > 0 && shouldShowScalapayWidget && (
              <button
                onClick={() => setIsScalapayExpanded((prev) => !prev)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 0",
                }}
                aria-expanded={isScalapayExpanded}
              >
                <img
                  src="/images/Scalapay_Logo_Primary_Black.png"
                  alt="Scalapay"
                  style={{ height: "28px", objectFit: "contain" }}
                />
              </button>
            )}
          </div>
          {activeStep > 0 ? (
            <div className="text-black">
              <div className="p-1 flex flex-col space-y-4">
                {!hideInsExtrasThirdTab && (
                  <>
                    <div
                      className="flex justify-between"
                      style={{
                        marginTop: "8px",
                      }}
                    >
                      <span className="font-semibold">
                        {" "}
                        {translate("Totale Noleggio:")}
                      </span>
                      <span>
                        {defaultCurrency || "€"}
                        {vehiclePrice?.toFixed(2) || 0}
                      </span>
                    </div>
                    {!appSettings?.donotShowInsurance && (
                      <div
                        className="flex justify-between"
                        style={{
                          marginTop: "8px",
                        }}
                      >
                        <span className="font-semibold">
                          {" "}
                          {translate("Totale Coperture:")}
                        </span>
                        <span>
                          {" "}
                          {defaultCurrency || "€"}
                          {insurancePrice?.toFixed(2) || 0}
                        </span>
                      </div>
                    )}
                    {!appSettings?.donotShowExtras && (
                      <div
                        className="flex justify-between"
                        style={{
                          marginTop: "8px",
                        }}
                      >
                        <span className="font-semibold">
                          {translate("Totale extras:")}
                        </span>
                        <span>
                          {" "}
                          {defaultCurrency || "€"}
                          {onlyExtrasPrice?.toFixed(2) || 0}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="p-4 flex justify-between font-bold p-1">
                <span>{translate("Totale")}</span>
                <span>
                  {" "}
                  {defaultCurrency || "€"}
                  {totalPrice?.toFixed(2) || 0}
                </span>
              </div>

              {shouldShowScalapayWidget && (
                <div
                  style={{
                    overflow: "hidden",
                    maxHeight: isScalapayExpanded ? "200px" : "0px",
                    transition: "max-height 0.3s ease",
                  }}
                >
                  <hr style={{ border: ".3px solid #ccc" }} />
                  <div
                    style={{
                      paddingTop: "4px",
                      fontSize: "12px",
                      color: "#000",
                    }}
                  >
                    <scalapay-widget
                      locale={localeScalapay}
                      channel="travel"
                      type="product"
                      amount={totalPrice.toFixed(2)}
                      merchant-token={scalapayMerchantToken}
                      {...(scalapayEnvVal !== ""
                        ? { environment: scalapayEnvVal }
                        : {})}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <span></span>
          )}
        </section>
      ),
    },
  ];
  /*------------------------------------------------------------------------------------------------------*/

  // ***********scalapay widget**********
  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    const shouldLoadScalapay =
      showScalapayWidgetPayNow || showScalapayWidgetPayLater;
    if (shouldLoadScalapay && scalapayMerchantToken) {
      script = document.createElement("script");
      script.src =
        "https://cdn.scalapay.com/widget/scalapay-widget-loader.js?version=V5";
      script.async = true;
      script.type = "module";
      script.onload = () => {};
      script.onerror = (e) => {
        console.error("Failed to load Scalapay widget script:", e);
      };
      document.head.appendChild(script);

      return () => {
        if (script && document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, [
    showScalapayWidgetPayNow,
    showScalapayWidgetPayLater,
    scalapayMerchantToken,
    scalapayEnvVal,
  ]);

  useEffect(() => {
    const filtersActive =
      Object.keys(filterValue.Transmission).length > 0 ||
      Object.keys(filterValue.status).length > 0 ||
      Object.keys(filterValue.category).length > 0 ||
      Object.keys(filterValue.type).length > 0 ||
      filterValue.TotalSeats.length > 0 ||
      Object.keys(filterValue.MacroGroup).length > 0 ||
      priceSortOrder !== "" ||
      showOnlyPremium === true;
    setAreFiltersActive(filtersActive);
  }, [filterValue, priceSortOrder, showOnlyPremium]);

  const { topVehicleCandidate, resolvedVehicleUpgrade } = useMemo(() => {
    const topVehicleCandidate = upgradeProposals?.vehicleUpgrade?.enabled
      ? upgradeProposals.vehicleUpgrade.candidates?.find((candidate) =>
          allVehicles.some(
            (v) => v.Vehicle?.groupPic?.nationalCode === candidate.groupCode
          )
        )
      : undefined;
    const matchingUpgradeVehicle = topVehicleCandidate
      ? allVehicles.find(
          (v) =>
            v.Vehicle?.groupPic?.nationalCode === topVehicleCandidate.groupCode
        )
      : undefined;
    const resolvedVehicleUpgrade = topVehicleCandidate
      ? {
          groupCode: topVehicleCandidate.groupCode,
          price: topVehicleCandidate.price,
          isDailyPrice: upgradeProposals?.vehicleUpgrade?.isDailyPrice,
          vehicleName:
            (appSettings?.showGroupWebDescription
              ? matchingUpgradeVehicle?.Vehicle?.groupWebDescription ||
                matchingUpgradeVehicle?.Vehicle?.VehMakeModel?.Name
              : matchingUpgradeVehicle?.Vehicle?.VehMakeModel?.Name) ||
            undefined,
          imageUrl:
            matchingUpgradeVehicle?.Vehicle?.vehicleGroupPic || undefined,
          specifications: matchingUpgradeVehicle?.Vehicle?.vehicleParameter
            ? [...matchingUpgradeVehicle.Vehicle.vehicleParameter]
                .sort((a, b) => Number(a.position) - Number(b.position))
                .map(
                  (param: {
                    fileUrl: string;
                    name: string;
                    position: string;
                  }) => ({
                    iconUrl: param.fileUrl,
                    name: param.name,
                  })
                )
            : [],
        }
      : undefined;
    return {
      topVehicleCandidate,
      resolvedVehicleUpgrade,
    };
  }, [
    upgradeProposals?.vehicleUpgrade,
    allVehicles,
    appSettings?.showGroupWebDescription,
  ]);

  const { resolvedCancellationUpgrade } = useMemo(() => {
    const matchingCancellationOption = upgradeProposals?.cancellationUpgrade
      ?.optionalCode
      ? selectedVehicle?.optionals?.find(
          (o) =>
            o.Equipment?.EquipType ===
            upgradeProposals.cancellationUpgrade.optionalCode
        )
      : undefined;
    const resolvedCancellationUpgrade =
      upgradeProposals?.cancellationUpgrade?.enabled &&
      matchingCancellationOption?.Charge?.Amount != null
        ? {
            ...upgradeProposals.cancellationUpgrade,
            price: matchingCancellationOption.Charge.Amount,
            description:
              matchingCancellationOption.Equipment?.Description || undefined,
          }
        : undefined;
    return { resolvedCancellationUpgrade };
  }, [upgradeProposals?.cancellationUpgrade, selectedVehicle?.optionals]);

  const originalInsuranceId =
    originalInsuranceBeforeUpgradeRef.current?.insuranceId ??
    selectedFields.insurance?.insuranceId;

  const { matchingProtectionInsurance, resolvedProtectionUpgrade } =
    useMemo(() => {
      const topProtectionCandidate = upgradeProposals?.protectionUpgrade
        ?.enabled
        ? upgradeProposals.protectionUpgrade.candidates?.find(
            (candidate) =>
              String(candidate.id) !== originalInsuranceId &&
              insuranceList["Booking Insurance"]?.some(
                (ins) => ins.Id === candidate.id
              )
          )
        : undefined;
      const matchingProtectionInsurance = topProtectionCandidate
        ? insuranceList["Booking Insurance"]?.find(
            (ins) => ins.Id === topProtectionCandidate.id
          )
        : undefined;
      const protectionUpgradeFeatures = matchingProtectionInsurance
        ? (insuranceList["Specification"] || []).map(
            (spec: { Description: string; BookingText?: string }) => {
              const indicatorKey = `${spec.Description.toUpperCase()}:${matchingProtectionInsurance.Description.toUpperCase()}`;
              const indicator =
                matchingProtectionInsurance.indicator?.[indicatorKey];
              return {
                label: spec.BookingText || spec.Description,
                included: indicator
                  ? indicator.toLowerCase() === "tick"
                  : false,
              };
            }
          )
        : [];
      const resolvedProtectionUpgrade =
        topProtectionCandidate && matchingProtectionInsurance
          ? {
              description: topProtectionCandidate.description,
              absolutePrice: parseFloat(
                matchingProtectionInsurance.Amount.toString()
              ),
              priceDelta:
                parseFloat(matchingProtectionInsurance.Amount.toString()) -
                (originalInsuranceBeforeUpgradeRef.current?.price ??
                  selectedFields.insurance?.price ??
                  0),
              features: protectionUpgradeFeatures,
            }
          : undefined;
      return { matchingProtectionInsurance, resolvedProtectionUpgrade };
    }, [
      upgradeProposals?.protectionUpgrade,
      originalInsuranceId,
      insuranceList,
      selectedFields.insurance?.price,
    ]);

  if (!appSettings) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          padding: "24px",
          boxSizing: "border-box",
        }}
      >
        <VehicleListSkeleton />
      </div>
    );
  }

  const handleUpgradeContinue = () => {
    dispatch(
      BOOKING_ACTIONS.setSelectedVehicleDetails({
        ...currentSelectedVehicleDetailsFromRedux,
        vehicleDetails: selectedVehicle,
        paymentDetails: {
          ...currentSelectedVehicleDetailsFromRedux?.paymentDetails,
          addedExtraOptions: selectedFields?.options,
          selectedInsurance: selectedFields?.insurance,
          price: totalPrice,
          extrasPrice: extrasPrice,
        },
      })
    );
    upgradeStepCompletedRef.current = true;
    setShowUpgradeProposals(false);
  };

  return (
    <ThemeProvider theme={dynamicTheme || theme}>
      <div
        className={`flex flex-col box-border ${
          isGretaBookingNewUi ? "" : "sm:px-6"
        }`}
        // className="flex flex-col box-border sm:px-6 gap-4 w-full"
        style={{
          background: appSettings?.isGretaBookingNewUi
            ? "#f5f5f5"
            : secondPageBackgroundColor
            ? secondPageBackgroundColor
            : "#ffffff",
          minHeight: isDesktopScreen
            ? "728px"
            : istabletScreen
            ? "1100px"
            : isSmallScreen
            ? "800px"
            : "",
        }}
      >
        {isFilterEnable &&
          (isSmallScreen || InTabletScreenFilter) &&
          activeStep == 0 && (
            <div
              style={
                isGretaBookingNewUi
                  ? {
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      marginTop: "6%",
                    }
                  : {
                      marginLeft: "90%",
                      marginTop:
                        isSmallScreen &&
                        isExpanded1 == false &&
                        isExpanded2 == false &&
                        isExpanded3 == false &&
                        isExpanded4 == false
                          ? "60%"
                          : isExpanded1 && isSmallScreen
                          ? "100%"
                          : InTabletScreenFilter
                          ? "0%"
                          : activeStep == 0 && !isExpanded1 && isSmallScreen
                          ? "100%"
                          : "60%",
                    }
              }
            >
              {!istabletScreen && (
                <FilterAlt
                  style={{
                    stroke: "black",
                    cursor: "pointer",
                  }}
                  onClick={() => setOpen(!open)}
                />
              )}
            </div>
          )}
        {reservedArea && <CollapsibleNavbar callFrom="showVehicle" />}
        {/*------------------------------------------------------tablet screen ui for card-----------------------------------------------------*/}
        {istabletScreen && (
          <>
            <div
              className="box-border py-4 flex flex-row hidden md:flex gap-1 sticky top-0 z-[10] w-full"
              style={{
                background: secondPageBackgroundColor
                  ? secondPageBackgroundColor
                  : "#ffffff",
              }}
            >
              <div className="box-border py-4 flex flex-row hidden md:flex gap-1 sticky top-0 z-[10] bg-[#FFFFFF] w-full">
                <button
                  style={{
                    color: visibleCards[0] === 0 ? "grey" : "black",
                  }}
                  onClick={handleBackwardClick}
                  disabled={visibleCards[0] === 0}
                >
                  <ArrowBackIosNewIcon />
                </button>
                {!isGretaBookingNewUi &&
                  cards.map((e, index) => {
                    if (
                      index === 2 &&
                      appSettings?.donotShowInsurance &&
                      appSettings?.donotShowExtras
                    ) {
                      return null;
                    }
                    if (visibleCards.includes(index) && index !== null) {
                      return (
                        <div key={index} className="w-1/2">
                          {e.label}
                        </div>
                      );
                    }
                    return null;
                  })}
                {/*  if (visibleCards.includes(index) && index !== null) {*/}
                {/*    return (*/}
                {/*      <div key={index} className="w-1/2">*/}
                {/*        {e.label}*/}
                {/*      </div>*/}
                {/*    );*/}
                {/*  }*/}
                {/*  return null;*/}
                {/*})}*/}
                <button
                  style={{
                    color:
                      visibleCards[1] === cards.length - 1 ||
                      selectedVehicle == undefined ||
                      activeStep != 2
                        ? "grey"
                        : "black",
                  }}
                  onClick={handleForwardClick}
                  disabled={
                    visibleCards[1] === cards.length - 1 ||
                    selectedVehicle == undefined ||
                    activeStep != 2
                  }
                >
                  <ArrowForwardIosIcon />
                </button>
              </div>
            </div>
          </>
        )}
        {/*-------------------------------------------mobile screen ui for card-------------------------------------------------------------------------------*/}
        {!isGretaBookingNewUi && isSmallScreen && (
          <div
            className={`${
              appSettings?.isRecapScrollableForMobile
                ? "absolute z-50"
                : "fixed z-50"
            } top-0 left-0 right-0 bg-white shadow-lg `}
          >
            <div
              className="flex flex-col p-2 grow h-full mt-5 bg-[#F4F4F4]"
              style={
                appSettings?.fillColorToCard
                  ? {
                      border: `2px solid ${dynamicTheme?.palette["primary"]?.main}`,
                      background: dynamicTheme?.palette["ternaryColor"]?.main,
                      borderRadius: appSettings?.recapHeaderBorderRadius
                        ? `${appSettings?.recapHeaderBorderRadius}rem`
                        : "0px",
                    }
                  : {
                      borderBottom: `5px solid ${dynamicTheme?.palette["ternaryColor"]?.main}`,
                      borderRadius: appSettings?.recapHeaderBorderRadius
                        ? `${appSettings?.recapHeaderBorderRadius}rem`
                        : "0px",
                    }
              }
            >
              <div className="mb-2">
                <div className="flex justify-between">
                  <span
                    className="text-xs text-white flex items-center justify-center mt-[5px]"
                    style={{
                      background: dynamicTheme?.palette["primary"]?.main,
                      width: "20px",
                      height: "20px",
                      borderRadius: "10px",
                    }}
                  >
                    1
                  </span>
                  <p
                    className="text-lg text-black grow ml-[15px]"
                    style={
                      appSettings?.fillColorToCard
                        ? {
                            color:
                              dynamicTheme?.palette["ternaryColor"]
                                .contrastText,
                          }
                        : {
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                          }
                    }
                  >
                    {translate("Selected Details")}
                  </p>
                  <button style={{ color: "black" }} onClick={toggleExpand1}>
                    {isExpanded1 ? <ExpandLess /> : <ExpandMore />}
                  </button>
                </div>

                {isExpanded1 && (
                  <>
                    <div>
                      <button
                        className="float-right"
                        style={{
                          color:
                            dynamicTheme?.palette["backgroundColor"]
                              .contrastText,
                          fontSize: "small",
                        }}
                        onClick={() => {
                          if (redirectUrl != null && redirectUrl !== "") {
                            window.location.href = redirectUrl;
                            hideIframe();
                          } else {
                            handleEdit();
                          }
                        }}
                      >
                        {t("Edit")}
                      </button>
                    </div>
                    <div>
                      <div className="flex justify-between items-start text-black mt-4">
                        <div className="flex-1 pt-2 pl-2">
                          <h6
                            className="text-sm whitespace-nowrap"
                            style={
                              appSettings?.fillColorToCard
                                ? {
                                    color:
                                      dynamicTheme?.palette["ternaryColor"]
                                        .contrastText,
                                  }
                                : {
                                    color:
                                      dynamicTheme?.palette["backgroundColor"]
                                        .contrastText,
                                  }
                            }
                          >
                            {pickUpLabel}
                          </h6>
                          <div
                            className="pt-3"
                            style={
                              appSettings?.fillColorToCard
                                ? {
                                    color:
                                      dynamicTheme?.palette["ternaryColor"]
                                        .contrastText,
                                  }
                                : {
                                    color:
                                      dynamicTheme?.palette["backgroundColor"]
                                        .contrastText,
                                  }
                            }
                          >
                            <span
                              style={{ fontSize: "large", fontWeight: "bold" }}
                            >
                              {bookingInformation.pickUpLocationName}
                            </span>
                          </div>
                          <div
                            className="whitespace-nowrap"
                            style={
                              appSettings?.fillColorToCard
                                ? {
                                    color:
                                      dynamicTheme?.palette["ternaryColor"]
                                        .contrastText,
                                  }
                                : {
                                    color:
                                      dynamicTheme?.palette["backgroundColor"]
                                        .contrastText,
                                  }
                            }
                          >
                            <p style={{ fontSize: "small" }}>
                              {t("date", {
                                date: new Date(
                                  bookingInformation.pickUpDateAndTime
                                ),
                              })}{" "}
                              {new Date(bookingInformation.pickUpDateAndTime)
                                .getHours()
                                .toString()
                                .padStart(2, "0")}
                              :
                              {new Date(bookingInformation.pickUpDateAndTime)
                                .getMinutes()
                                .toString()
                                .padStart(2, "0")}
                            </p>
                          </div>
                        </div>

                        <div className="flex-1 pl-16 pt-2">
                          <h6
                            className="text-sm whitespace-nowrap"
                            style={
                              appSettings?.fillColorToCard
                                ? {
                                    color:
                                      dynamicTheme?.palette["ternaryColor"]
                                        .contrastText,
                                  }
                                : {
                                    color:
                                      dynamicTheme?.palette["backgroundColor"]
                                        .contrastText,
                                  }
                            }
                          >
                            {dropOffLabel}
                          </h6>
                          <div
                            className="pt-3"
                            style={
                              appSettings?.fillColorToCard
                                ? {
                                    color:
                                      dynamicTheme?.palette["ternaryColor"]
                                        .contrastText,
                                  }
                                : {
                                    color:
                                      dynamicTheme?.palette["backgroundColor"]
                                        .contrastText,
                                  }
                            }
                          >
                            <span
                              style={{ fontSize: "large", fontWeight: "bold" }}
                            >
                              {bookingInformation.dropOffLocationName}
                            </span>
                          </div>
                          <div
                            className="whitespace-nowrap"
                            style={
                              appSettings?.fillColorToCard
                                ? {
                                    color:
                                      dynamicTheme?.palette["ternaryColor"]
                                        .contrastText,
                                  }
                                : {
                                    color:
                                      dynamicTheme?.palette["backgroundColor"]
                                        .contrastText,
                                  }
                            }
                          >
                            <p style={{ fontSize: "small" }}>
                              {t("date", {
                                date: new Date(
                                  bookingInformation.dropOffDateAndTime
                                ),
                              })}{" "}
                              {new Date(bookingInformation.dropOffDateAndTime)
                                .getHours()
                                .toString()
                                .padStart(2, "0")}
                              :
                              {new Date(bookingInformation.dropOffDateAndTime)
                                .getMinutes()
                                .toString()
                                .padStart(2, "0")}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div>
                        {searchParams.get("agreementCoupon") && (
                          <div style={{ color: "red", paddingLeft: "9px" }}>
                            <p>
                              Codice promo:{" "}
                              {searchParams.get("agreementCoupon")}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {(searchParams.get("reservationSourcePayNow") === "true" ||
                  searchParams.get("reservationSourcePayLater") === "true") && (
                  <div
                    className="flex "
                    style={
                      appSettings?.fillColorToCard
                        ? {
                            color:
                              dynamicTheme?.palette["ternaryColor"]
                                .contrastText,
                          }
                        : {
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                          }
                    }
                  >
                    <span className="pl-2" style={{ color: "red" }}>
                      {" "}
                      {translate("Mode")}
                    </span>
                    {"  "}
                    {searchParams.get("reservationSourcePayNow") === "true" && (
                      <span style={{ color: "red", marginLeft: "10px" }}>
                        {" "}
                        {translate("Pay Now")}
                      </span>
                    )}
                    {"  "}
                    {searchParams.get("reservationSourcePayLater") ===
                      "true" && (
                      <span style={{ color: "red", marginLeft: "10px" }}>
                        {" "}
                        {translate("Pay Later")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div
              className="flex flex-col p-2 grow h-full"
              style={
                appSettings?.fillColorToCard
                  ? {
                      border: `2px solid ${dynamicTheme?.palette["primary"].main}`,
                      borderRadius: appSettings?.recapHeaderBorderRadius
                        ? `${appSettings?.recapHeaderBorderRadius}rem`
                        : "0px",
                      background:
                        activeStep > 0
                          ? dynamicTheme?.palette["ternaryColor"].main
                          : "inherit",
                    }
                  : {
                      border: `1px solid #cccccc`,
                      borderRadius: appSettings?.recapHeaderBorderRadius
                        ? `${appSettings?.recapHeaderBorderRadius}rem`
                        : "0px",
                      borderBottom:
                        activeStep > 0
                          ? `4px solid ${dynamicTheme?.palette["ternaryColor"].main}`
                          : `4px solid #cccccc`,
                      borderTop: "none",
                      background: recapCardBackgroundColor
                        ? recapCardBackgroundColor
                        : "#ffffff",
                    }
              }
            >
              <div className="flex justify-between">
                <span
                  className="text-xs text-white flex items-center justify-center mt-[5px]"
                  style={{
                    background: dynamicTheme?.palette["primary"]?.main,
                    width: "20px",
                    height: "20px",
                    borderRadius: "10px",
                  }}
                >
                  2
                </span>
                <p
                  className="text-lg text-black grow ml-[15px]"
                  style={
                    {
                      color:
                        activeStep > 0 && appSettings?.fillColorToCard
                          ? dynamicTheme?.palette["ternaryColor"].contrastText
                          : dynamicTheme?.palette["backgroundColor"]
                              .contrastText,
                    } && activeStep == 0
                      ? { fontWeight: "bold" }
                      : { fontWeight: "normal" }
                  }
                >
                  {t("Car Selection")}
                </p>
                <button style={{ color: "black" }} onClick={toggleExpand2}>
                  {isExpanded2 ? <ExpandLess /> : <ExpandMore />}
                </button>
              </div>
              {selectedVehicle && isExpanded2 ? (
                <div>
                  <button
                    className={"float-right"}
                    style={{
                      color:
                        dynamicTheme?.palette["backgroundColor"].contrastText,
                    }}
                    onClick={() => {
                      if (activeStep > 0) {
                        if (activeStep == 2) {
                          window.history.pushState({ step: 2 }, "Step 2", "");
                        }
                        setActiveStep(0);
                      }
                      setIsEdit(true);
                      setIsLoading(false);
                      setIsNotClickedEdit(false);
                      setSelectedVehicle(undefined);
                    }}
                  >
                    {t("Edit")}
                  </button>
                  <div className="flex flex-row text-black">
                    <div className="p-2 text-left">
                      <h4
                        className="text-md font-bold"
                        style={{
                          color:
                            activeStep > 0 && appSettings?.fillColorToCard
                              ? dynamicTheme?.palette["ternaryColor"]
                                  .contrastText
                              : dynamicTheme?.palette["backgroundColor"]
                                  .contrastText,
                        }}
                      >
                        {t("Vehicle")}
                      </h4>
                      <div
                        className="pt-1"
                        style={{
                          fontSize: "small",
                          color:
                            activeStep > 0 && appSettings?.fillColorToCard
                              ? dynamicTheme?.palette["ternaryColor"]
                                  .contrastText
                              : dynamicTheme?.palette["backgroundColor"]
                                  .contrastText,
                        }}
                      >
                        {selectedVehicle.Vehicle?.VehMakeModel?.Name}
                        <span>
                          {selectedVehicle.Vehicle?.vehicelExtraImage}
                        </span>
                      </div>
                    </div>
                    <div className="p-2 text-left">
                      <h4 className="text-md text-[primary] font-bold"></h4>
                      <div className="pt-1">
                        <span className="pt-1 pb-1 mr-1">
                          <img
                            src={
                              (selectedVehicle.Vehicle?.vehicleGroupPic ||
                                selectedVehicle.Vehicle
                                  ?.vehicelExtraImage) as string
                            }
                            className="md:min-w-full aspect-video object-contain h-24"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
            {!hideInsExtrasThirdTab && (
              <section
                className="flex flex-col h-full w-full grow p-2 grow h-full"
                style={
                  appSettings?.fillColorToCard
                    ? {
                        border: `2px solid ${dynamicTheme?.palette["primary"].main}`,
                        borderRadius: appSettings?.recapHeaderBorderRadius
                          ? `${appSettings?.recapHeaderBorderRadius}rem`
                          : "0px",
                        background:
                          activeStep > 1
                            ? dynamicTheme?.palette["ternaryColor"].main
                            : "inherit",
                      }
                    : {
                        borderRadius: appSettings?.recapHeaderBorderRadius
                          ? `${appSettings?.recapHeaderBorderRadius}rem`
                          : "0px",
                        borderBottom:
                          activeStep > 1
                            ? `4px solid ${dynamicTheme?.palette["ternaryColor"].main}`
                            : `4px solid #cccccc`,
                        borderTop: "none",
                        background: recapCardBackgroundColor
                          ? recapCardBackgroundColor
                          : "#ffffff",
                      }
                }
              >
                <div className="flex flex-row grow">
                  <span
                    className="text-xs text-white flex items-center justify-center mt-[5px]"
                    style={{
                      background: dynamicTheme?.palette["primary"]?.main,
                      width: "20px",
                      height: "20px",
                      borderRadius: "10px",
                    }}
                  >
                    3
                  </span>
                  <p
                    className="text-lg text-black grow ml-[15px]"
                    style={
                      {
                        color:
                          activeStep > 1 && appSettings?.fillColorToCard
                            ? dynamicTheme?.palette["ternaryColor"].contrastText
                            : dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                      } && activeStep == 1
                        ? { fontWeight: "bold" }
                        : { fontWeight: "normal" }
                    }
                  >
                    Extra
                  </p>
                  <button style={{ color: "black" }} onClick={toggleExpand3}>
                    {isExpanded3 ? <ExpandLess /> : <ExpandMore />}
                  </button>
                </div>
                {(selectedFields.insurance ||
                  includedIns.length ||
                  Object.values(selectedFields.options).length) &&
                isExpanded3 ? (
                  <div
                    style={{
                      maxHeight: "150px",
                      overflowY: "auto",
                    }}
                  >
                    <button
                      className="h-fit float-right"
                      style={{
                        color:
                          dynamicTheme?.palette["backgroundColor"].contrastText,
                      }}
                      onClick={() => {
                        if (activeStep > 1) {
                          setActiveStep(1);
                          setPushState(1);
                          setIsNotClickedEdit(false);
                          setIsLoading(false);
                          setShowUpgradeProposals(false);
                        }
                      }}
                    >
                      {t("Edit")}
                    </button>
                    <div className="flex flex-row text-black mb-[15%]">
                      {(selectedFields.insurance || includedIns.length > 0) && (
                        <div>
                          <div className="p-4" style={{ paddingBottom: "0px" }}>
                            {includedIns.length > 0 && (
                              <div>
                                <h4
                                  className="text-md font-bold"
                                  style={{
                                    color:
                                      activeStep > 1 &&
                                      appSettings?.fillColorToCard
                                        ? dynamicTheme?.palette["ternaryColor"]
                                            .contrastText
                                        : dynamicTheme?.palette[
                                            "backgroundColor"
                                          ].contrastText,
                                  }}
                                >
                                  {translate("Included insurance")}
                                </h4>
                                <span
                                  className="pt-1 pb-1"
                                  style={{ fontSize: "small", color: "black" }}
                                >
                                  {includedIns
                                    .map((ins) => ins?.Description)
                                    .join(", ")}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="p-4 text-left whitespace-nowrap w-[50%]">
                            {selectedFields.insurance && (
                              <>
                                <h4
                                  className="text-md font-bold"
                                  style={{
                                    color:
                                      activeStep > 1 &&
                                      appSettings?.fillColorToCard
                                        ? dynamicTheme?.palette["ternaryColor"]
                                            .contrastText
                                        : dynamicTheme?.palette[
                                            "backgroundColor"
                                          ].contrastText,
                                  }}
                                >
                                  {t("Insurance Added")}
                                </h4>
                                <div
                                  className="pt-1"
                                  style={{
                                    color:
                                      activeStep > 1 &&
                                      appSettings?.fillColorToCard
                                        ? dynamicTheme?.palette["ternaryColor"]
                                            .contrastText
                                        : dynamicTheme?.palette[
                                            "backgroundColor"
                                          ].contrastText,
                                  }}
                                >
                                  <span
                                    className="pt-1 pb-1"
                                    style={{ fontSize: "small" }}
                                  >
                                    {selectedFields.insurance?.details}
                                  </span>
                                  <span style={{ fontSize: "small" }}>
                                    {selectedFields.insurance?.name}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    color:
                                      activeStep > 1 &&
                                      appSettings?.fillColorToCard
                                        ? dynamicTheme?.palette["ternaryColor"]
                                            .contrastText
                                        : dynamicTheme?.palette[
                                            "backgroundColor"
                                          ].contrastText,
                                  }}
                                >
                                  <span style={{ fontSize: "small" }}>
                                    {defaultCurrency || "€"}
                                    {selectedFields.insurance?.price}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {Object.keys(selectedFields.options).length > 0 && (
                        <div
                          className="p-4 text-left whitespace-nowrap mt-2 w-[50%]"
                          style={{
                            marginLeft: selectedFields.insurance ? "5%" : "",
                          }}
                        >
                          <h4
                            className="text-md font-bold"
                            style={{
                              color:
                                activeStep > 1 && appSettings?.fillColorToCard
                                  ? dynamicTheme?.palette["ternaryColor"]
                                      .contrastText
                                  : dynamicTheme?.palette["backgroundColor"]
                                      .contrastText, // Set color based on condition
                            }}
                          >
                            {t("Added Extras")}
                          </h4>
                          <div
                            className="pt-1"
                            style={{
                              color:
                                activeStep > 1 && appSettings?.fillColorToCard
                                  ? dynamicTheme?.palette["ternaryColor"]
                                      .contrastText
                                  : dynamicTheme?.palette["backgroundColor"]
                                      .contrastText,
                            }}
                          >
                            <span
                              className="pt-1 pb-1"
                              style={{ fontSize: "small" }}
                            >
                              {Object.entries(selectedFields.options).map(
                                ([key, value]) => (
                                  <div key={key} className="flex flex-row">
                                    {(() => {
                                      const text =
                                        appSettings?.showDescFromBookingText
                                          ? data.find(
                                              (item) => item.key === value?.name
                                            )?.description || value?.name
                                          : appSettings?.showOptionalDescription
                                          ? value?.details
                                          : value?.name;

                                      return !isSmallScreen ? (
                                        text?.length <= 20
                                      ) : text?.length <= 10 ? (
                                        <span className="flex flex-row w-[130%] md:w-[50%]">
                                          {text} {"   "}
                                        </span>
                                      ) : (
                                        <span className="marquee-container">
                                          <div className="marquee-content">
                                            <span className="marquee-item">
                                              {text} {"   "}
                                            </span>
                                            <span className="marquee-item">
                                              {text} {"   "}
                                            </span>
                                          </div>
                                        </span>
                                      );
                                    })()}
                                    <span
                                      className="whitespace-nowrap flex flex-row font-bold ml-[10%]"
                                      style={{ fontWeight: "bold" }}
                                    >
                                      {!value.doNotShowOptionalPrice && (
                                        <>
                                          {defaultCurrency || "€"}
                                          {(
                                            value?.price * value?.quantity
                                          ).toFixed(2)}
                                        </>
                                      )}
                                    </span>
                                  </div>
                                )
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <span>
                    {(Object.keys(selectedFields.options).length == 0 ||
                      selectedFields.insurance == null) &&
                      isExpanded3 &&
                      activeStep == 2 && (
                        <button
                          className="h-fit float-right"
                          style={{
                            color:
                              dynamicTheme?.palette["backgroundColor"]
                                .contrastText,
                          }}
                          onClick={() => {
                            if (activeStep > 1) {
                              setActiveStep(1);
                              setPushState(1);
                              setIsNotClickedEdit(false);
                              setIsLoading(false);
                              setShowUpgradeProposals(false);
                            }
                          }}
                        >
                          {t("Edit")}
                        </button>
                      )}
                  </span>
                )}
              </section>
            )}
            <section
              className="flex flex-col h-full w-full grow p-2 grow h-full"
              style={
                appSettings?.fillColorToCard
                  ? {
                      border: `2px solid ${dynamicTheme?.palette["primary"]?.main}`,
                      background: "none",
                      borderRadius: appSettings?.recapHeaderBorderRadius
                        ? `${appSettings?.recapHeaderBorderRadius}rem`
                        : "0px",
                    }
                  : {
                      border: `1px solid #cccccc`,
                      borderBottom: `4px solid #cccccc`,
                      borderTop: "none",
                      borderRadius: appSettings?.recapHeaderBorderRadius
                        ? `${appSettings?.recapHeaderBorderRadius}rem`
                        : "0px",
                      background: recapCardBackgroundColor
                        ? recapCardBackgroundColor
                        : "#ffffff",
                    }
              }
            >
              <div className="flex flex-row justify-between items-center w-full">
                <span
                  className="text-xs text-white flex items-center justify-center mt-[5px]"
                  style={{
                    background: dynamicTheme?.palette["primary"]?.main,
                    width: "20px",
                    height: "20px",
                    borderRadius: "10px",
                  }}
                >
                  {!hideInsExtrasThirdTab ? "4" : "3"}
                </span>
                <p
                  className="text-lg text-black grow ml-[10px]"
                  style={
                    {
                      color:
                        activeStep > 2
                          ? dynamicTheme?.palette["backgroundColor"]
                              ?.contrastText
                          : dynamicTheme?.palette["backgroundColor"]
                              ?.contrastText,
                    } && activeStep == 2
                      ? { fontWeight: "bold" }
                      : { fontWeight: "normal" }
                  }
                >
                  {translate("Total Rental")}
                </p>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {activeStep > 0 && shouldShowScalapayWidget && (
                    <button
                      onClick={() => setIsScalapayExpanded((prev) => !prev)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "2px 0",
                      }}
                      aria-expanded={isScalapayExpanded}
                    >
                      <img
                        src="/images/Scalapay_Logo_Primary_Black.png"
                        alt="Scalapay"
                        style={{ height: "28px", objectFit: "contain" }}
                      />
                    </button>
                  )}
                  <button style={{ color: "black" }} onClick={toggleExpand4}>
                    {isExpanded4 ? <ExpandLess /> : <ExpandMore />}
                  </button>
                </div>
              </div>
              {activeStep > 0 && isExpanded4 ? (
                <div className="flex flex-col text-black">
                  {!hideInsExtrasThirdTab && (
                    <>
                      <div className="flex justify-between">
                        <span className="pt-1 pb-1">
                          {translate("Totale Noleggio:")}
                        </span>
                        <span className="pt-1 pb-1">
                          {defaultCurrency || "€"}
                          {vehiclePrice?.toFixed(2) || 0}
                        </span>
                      </div>
                      {!appSettings?.donotShowInsurance && (
                        <div className="flex justify-between">
                          <span className="pt-1 pb-1">
                            {translate("Totale Coperture:")}
                          </span>
                          <span className="pt-1 pb-1">
                            {defaultCurrency || "€"}
                            {insurancePrice?.toFixed(2) || 0}
                          </span>
                        </div>
                      )}
                      {!appSettings?.donotShowExtras && (
                        <div className="flex justify-between">
                          <span className="pt-1 pb-1">
                            {translate("Totale extras:")}
                          </span>
                          <span className="pt-1 pb-1">
                            {defaultCurrency || "€"}
                            {onlyExtrasPrice?.toFixed(2) || 0}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-between font-bold">
                    <span className="pt-1 pb-1"> {translate("Totale")}</span>
                    <span className="pt-1 pb-1">
                      {defaultCurrency || "€"}
                      {selectedPrice?.toFixed(2) || 0}
                    </span>
                  </div>

                  {shouldShowScalapayWidget && (
                    <div
                      style={{
                        overflow: "hidden",
                        maxHeight: isScalapayExpanded ? "200px" : "0px",
                        transition: "max-height 0.3s ease",
                      }}
                    >
                      <div
                        style={{
                          paddingTop: "4px",
                          fontSize: "12px",
                          color: "#000",
                        }}
                      >
                        <scalapay-widget
                          locale={localeScalapay}
                          channel="travel"
                          type="product"
                          amount={totalPrice.toFixed(2)}
                          merchant-token={scalapayMerchantToken}
                          {...(scalapayEnvVal !== ""
                            ? { environment: scalapayEnvVal }
                            : {})}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <span></span>
              )}
            </section>
          </div>
        )}
        {/*-----------------------------------------------------------card for desktop view----------------------------------------------------------------------------------*/}
        {!isGretaBookingNewUi && isDesktopScreen && (
          <div
            className={`box-border py-4 flex flex-row hidden md:flex gap-1 sticky ${
              appSettings?.headerAndFooter ? "top-10" : "top-0"
            } z-[10] w-full`}
            style={{
              background: secondPageBackgroundColor
                ? secondPageBackgroundColor
                : "#ffffff",
              borderRadius: appSettings?.recapHeaderBorderRadius
                ? `${appSettings?.recapHeaderBorderRadius}rem`
                : "0px",
            }}
          >
            {cards.map((e, index) => {
              const className = !hideInsExtrasThirdTab ? "w-1/4" : "w-1/3";
              if (index === 2 && hideInsExtrasThirdTab) {
                return null;
              }
              return (
                <div key={index} className={className}>
                  {e.label}
                </div>
              );
            })}
          </div>
        )}
        {/*-------------------------------------------------------------------------------------------------*/}
        {activeStep == 0 && (
          <>
            {isGretaBookingNewUi && !isEditOpen && (
              <BookingSummaryNavbar
                bookingDetails={bookingInformation}
                onEdit={handleEditOpen}
              />
            )}
            <div ref={bookingFormRef}>
              <Collapse
                in={isEditOpen}
                unmountOnExit
                onEntered={handleCollapseEntered}
                style={{ position: "relative", zIndex: 20 }}
              >
                <div className="w-full bg-white px-4 py-6 shadow-lg">
                  {isEnableHorizontalView !== null && BookingFormConfig.fields && (
                    <BookingForm
                      className="grow"
                      isEnableHorizontalView={isEnableHorizontalView}
                      options={BookingFormConfig.fields || []}
                      onSubmit={(e) => {
                        dispatch(
                          BOOKING_ACTIONS.setBookingInformation({ ...e })
                        );
                        setIsEditOpen(false);
                        setIsLoading(true);
                        const newParams = createSearchParams({
                          dropOffDateAndTime: e.dropOffDateAndTime,
                          pickUpDateAndTime: e.pickUpDateAndTime,
                          pickUpLocation: e.pickUpLocationDetails.code,
                          dropOffLocation: e.dropOffLocationDetails.code,
                          pickUpLocationName: e.pickUpLocationDetails.name,
                          dropOffLocationName: e.dropOffLocationDetails.name,
                          reservationSource: e.reservationSource.code,
                          macroGroupName: e.macroGroupName,
                          agreementCoupon: e.agreementCoupon,
                          showPics: "true",
                          showOptionalImage: "true",
                          showVehicleParameter: "true",
                          showVehicleExtraImage: "true",
                          showBookingDiscount: "true",
                          isYoungDriverAge: e.isYoungDriverAge,
                          isYoungDriver2Age: e.isYoungDriver2Age,
                          isSeniorDriverAge: e.isSeniorDriverAge,
                          noFeeAge: e.noFeeAge,
                          isAgeLimitSelected: e.isAgeLimitSelected,
                          reservationSourcePayNow:
                            e.reservationSource.reservationSourcePayNow,
                          reservationSourcePayLater:
                            e.reservationSource.reservationSourcePayLater,
                          youngDriverAgeValue: e.youngDriverAgeValue,
                          youngDriver2AgeValue: e.youngDriver2AgeValue,
                          seniorDriverAgeValue: e.seniorDriverAgeValue,
                          noAgeDriverValue: e.noAgeDriverValue,
                          affiliateName:
                            e.affiliateDetails.name !== ""
                              ? e.affiliateDetails.name
                              : null,
                          affiliateId:
                            e.affiliateDetails.code !== ""
                              ? e.affiliateDetails.code
                              : null,
                          isAirport: e.pickUpLocationDetails.isAirport,
                          scriptChannel: e?.scriptChannel ?? "",
                          scriptAffiliate: e?.scriptAffiliate ?? "",
                          scriptVoucherCode: e?.scriptVoucherCode ?? "",
                          reservedAreaPayNow: e?.reservedAreaPayNow,
                          ...encodeDeliveryUrlParams(e),
                        }).toString();
                        persistDeliveryDetails(e);
                        navigation("/choose/vehicle?" + newParams);
                      }}
                    />
                  )}
                </div>
              </Collapse>
            </div>
            {isEditOpen && (
              <div
                onClick={() => setIsEditOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  zIndex: 10,
                }}
              />
            )}

            {isGretaBookingNewUi && vehiclePageTitle && (
              <>
                <h2
                  className="font-bold text-2xl text-black px-2 gap-4 uppercase"
                  style={{
                    padding: isSmallScreen
                      ? "16px 16px 16px 16px"
                      : appSettings?.showThreeVehicleCardInArow
                      ? "0px 40px 0px 40px"
                      : "15px 90px 15px 90px",
                    marginLeft: isDesktopScreen ? "40px" : "0px",
                  }}
                >
                  {vehiclePageTitle}
                </h2>

                <hr
                  style={{
                    border: "none",
                    borderTop: "3px solid #e5e7eb",
                  }}
                />
              </>
            )}
            {showFilterAtTop &&
            isFilterEnable &&
            (isDesktopScreen || istabletScreen) ? (
              <VehicleFiltersAtTop />
            ) : (
              <div className="h-4" />
            )}
            <div
              className={`flex flex-row gap-4 mb-4 ${
                isGretaBookingNewUi ? "sm:px-8" : ""
              }`}
              style={
                !appSettings?.showThreeVehicleCardInArow &&
                isGretaBookingNewUi &&
                !isSmallScreen
                  ? { padding: istabletScreen ? "0 1rem" : "" }
                  : undefined
              }
            >
              <main className={getClassName()}>
                {isResolvingUpgradeOffers && (
                  <div className="flex flex-col justify-center items-center w-full py-4">
                    <CircularProgress />
                  </div>
                )}
                {(
                  appSettings?.enableCustomLoader
                    ? !isLoading && vehiclesReady
                    : !isLoading
                ) ? (
                  <div
                    style={
                      isResolvingUpgradeOffers
                        ? { pointerEvents: "none", opacity: 0.6 }
                        : undefined
                    }
                  >
                    <ShowVehicles
                      onVehicleSelect={async (
                        e: StateSelectedVehicleDetails
                      ) => {
                        setSelectedVehicle(e.vehicleDetails);
                        setCardPaymentChoice(
                          e.paymentDetails?.type === "PAYNOW" ||
                            e.paymentDetails?.type === "PAYLATER"
                            ? {
                                type: e.paymentDetails.type,
                                price: e.paymentDetails.price,
                                isDiscounted: e.paymentDetails.isDiscounted,
                                surchargeAmt: e.paymentDetails.surchargeAmt,
                              }
                            : null
                        );
                        try {
                          setIsInsuranceLoading(true);
                          const insuranceDetails =
                            await BookingApi.getInsuranceDetails(
                              ReservationDetails,
                              e.vehicleDetails.Vehicle?.VendorCarType || "",
                              e.vehicleDetails.optionals || []
                            );
                          const insuranceList =
                            insuranceDetails["Booking Insurance"] ?? [];
                          const noInsurance = insuranceList.length === 0;
                          const noExtras =
                            e.vehicleDetails?.optionals?.length === 0;

                          const hasSingleMatchingOptional =
                            e.vehicleDetails?.optionals?.length === 1 &&
                            insuranceList.some(
                              (insurance) =>
                                insurance.optionalsCode ===
                                e.vehicleDetails.optionals?.[0]?.Equipment.Code
                            ) &&
                            e.vehicleDetails.optionals?.[0]?.Charge?.Amount ===
                              0;

                          hideTab =
                            (noInsurance && appSettings?.donotShowExtras) ||
                            (noInsurance && noExtras) ||
                            (appSettings?.donotShowInsurance &&
                              noExtras &&
                              hasSingleMatchingOptional) ||
                            (appSettings?.donotShowInsurance &&
                              hasSingleMatchingOptional &&
                              !appSettings?.donotShowExtras);
                          if (hideTab) {
                            setHideInsExtrasThirdTab(true);
                          }
                          setInsuranceList(insuranceDetails);
                          setGroupName(
                            e.vehicleDetails.Vehicle?.VehMakeModel?.Name || ""
                          );
                          if (insuranceList.length > 0) {
                            insuranceList.forEach((item) => {
                              findTextForInsuranceBody(
                                item.Description,
                                e.vehicleDetails.Vehicle?.VehMakeModel?.Name
                              );
                            });
                          }
                          if (
                            !hideTab &&
                            !(
                              appSettings?.donotShowInsurance &&
                              appSettings?.donotShowExtras
                            )
                          ) {
                            setShowUpgradeProposals(false);
                            setActiveStep(1);
                            window.history.pushState({ step: 1 }, "Step 1", "");
                          } else {
                            await handleStepTwoActions(e.vehicleDetails);
                          }
                        } catch (error) {
                          console.error(
                            "Error fetching insurance details:",
                            error
                          );

                          setShowUpgradeProposals(false);
                          setActiveStep(1);
                          window.history.pushState({ step: 1 }, "Step 1", "");
                        } finally {
                          setIsInsuranceLoading(false);
                        }
                        setDiscountValueWithoutVat(
                          e?.vehicleDetails?.Reference?.ID_without_vat
                            ? e?.vehicleDetails?.Reference?.ID_without_vat?.toString()
                            : e?.vehicleDetails?.Reference?.ID?.toString()
                        );
                      }}
                      allVehicles={allVehicles}
                      showOnlyPremium={showOnlyPremium}
                      vehiclesFilter={filterValue}
                      priceSortOrder={priceSortOrder}
                      priceLowToHighValue={priceLowToHighValue}
                      priceHighToLowValue={priceHighToLowValue}
                      className="animate__animated animate__slideInUp"
                      textColor={
                        dynamicTheme?.palette["secondaryColor"]?.contrastText
                      }
                      reservationSourcePayNow={reservationSourcePayNow}
                      reservationSourcePayLater={reservationSourcePayLater}
                      backgroundColor={
                        dynamicTheme?.palette["backgroundColor"]?.main
                      }
                      backgroundTextColor={
                        dynamicTheme?.palette["secondaryColor"]?.main
                      }
                      errorMsgNoVehicle={errorMsgNoVehicle}
                      isExpanded1={isExpanded1}
                      isExpanded2={isExpanded2}
                      isExpanded3={isExpanded3}
                      isExpanded4={isExpanded4}
                      isFilterEnable={isFilterEnable}
                      showScalapayWidgetPayLater={showScalapayWidgetPayLater}
                      showScalapayWidgetPayNow={showScalapayWidgetPayNow}
                      scalapayMerchantToken={scalapayMerchantToken}
                      scalapayEnvVal={scalapayEnvVal}
                      isNotclickedEdit={isNotclickedEdit}
                      isVehicleUnavQuot={isVehicleUnavQuot}
                      setIsVehicleUnavQuot={setIsVehicleUnavQuot}
                      setIsLoading={setIsLoading}
                      areFiltersActive={areFiltersActive}
                      primaryColor={dynamicTheme?.palette["primary"]?.main}
                    />
                  </div>
                ) : appSettings?.enableCustomLoader ? (
                  <VehicleAvailabilityLoader
                    isLoading={isLoading}
                    onComplete={() => setVehiclesReady(true)}
                  />
                ) : (
                  <VehicleListSkeleton />
                )}
              </main>
              {isFilterEnable && !showFilterAtTop ? (
                <aside
                  className="w-80 sticky top-0 lg:flex hidden flex-col cursor-pointer bg-[#FFFFFF]"
                  style={{
                    borderRadius: "10px",
                    backgroundColor: "white",
                    marginTop: "1%",
                  }}
                >
                  <VehicleFilters />
                </aside>
              ) : null}
              {/* Mobile filter slide-in panel */}
              {open && (
                <div
                  className="fixed inset-0 z-40"
                  style={{ background: "rgba(0,0,0,0.3)" }}
                  onClick={handleDrawerToggle}
                />
              )}
              <div
                className="fixed top-0 right-0 h-full z-50 bg-white shadow-2xl overflow-y-auto"
                style={{
                  width: "75vw",
                  maxWidth: "300px",
                  transform: open ? "translateX(0)" : "translateX(100%)",
                  transition: "transform 0.3s ease-in-out",
                }}
              >
                <div className="flex justify-end p-2">
                  <CloseIcon
                    style={{ cursor: "pointer", color: "#555" }}
                    onClick={handleDrawerToggle}
                  />
                </div>
                <VehicleFilters />
              </div>
            </div>
          </>
        )}
        <section className="hover:drop-shadow-lg cursor-pointer rounded-tl-[32px] rounded-br-[12px] rounded-bl-[12px] relative flex flex-col bg-white  box-border text-[#004188] z-12">
          {showInfo && (
            <div className="hover:drop-shadow-lg cursor-pointer rounded-tl-[32px] rounded-br-[12px] rounded-bl-[12px] h-full w-full absolute bg-white p-4 z-10 top-0 left-0 animate__animated animate__zoomIn">
              <div>
                <Button
                  className="w-1/3 flex justify-end"
                  onClick={() => {
                    toogleVehicleInfoCardShowingStatus();
                  }}
                >
                  close
                </Button>
                <p>This is the info that should be displayed on the card.</p>
              </div>
            </div>
          )}
        </section>
        {activeStep == 1 && !hideInsExtrasThirdTab && !showUpgradeProposals && (
          <div
            className={`h-full w-full animate__animated animate__slideInUp ${
              isSmallScreen ? "mt-[5%]" : ""
            }`}
            style={
              isResolvingUpgradeOffers
                ? { pointerEvents: "none", opacity: 0.6 }
                : undefined
            }
          >
            {isResolvingUpgradeOffers && (
              <div className="flex flex-col justify-center items-center w-full py-4">
                <CircularProgress />
              </div>
            )}
            {/* Greta New UI Header */}
            {isGretaBookingNewUi && (
              <GretaHeader
                title={whichProtectionPackageText}
                totalPrice={totalPrice ?? 0}
                currency={defaultCurrency || "€"}
                onBack={() => {
                  setActiveStep(0);
                  setPushState(0);
                  setIsLoading(false);
                }}
                onPriceDetailClick={() => setShowPriceDetail(true)}
                translate={translate}
                priceDetailsText={priceDetailsText}
                showContinueButton={true}
                continueButtonStyle={{
                  background: appSettings?.continueButtonPrimaryColor
                    ? dynamicTheme?.palette["primary"]?.main
                    : "",

                  height: "48px",
                  minWidth: "120px",
                  padding: "8px 24px",
                  borderRadius: `${appSettings?.buttonBorderRadius ?? 50}px`,
                }}
                onContinue={() => {
                  if (shouldShowNoCoverageWarning()) {
                    pendingProceedTypeRef.current =
                      userId != null && userId != "null" && userId != ""
                        ? reservedAreaPayNow == "true"
                          ? "PAYNOW"
                          : "PAYLATER"
                        : "";
                    setShowNoCoverageWarning(true);
                    return;
                  }
                  scrollToTop();
                  window.scrollTo(0, 0);
                  if (selectedVehicle) {
                    dispatch(
                      BOOKING_ACTIONS.setSelectedVehicleDetails({
                        paymentDetails: {
                          addedExtraOptions: selectedFields?.options,
                          isDiscounted:
                            searchParams.get("reservationSourcePayLater") ===
                              "true" ||
                            (userId != null && userId != "null" && userId != "")
                              ? 0
                              : (parseFloat(
                                  (selectedVehicle?.Reference?.ID &&
                                  selectedVehicle?.Reference?.ID > 0
                                    ? (
                                        selectedVehicle?.Reference
                                          ?.originalAmount -
                                        selectedVehicle?.Reference.ID
                                      ).toString()
                                    : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) ||
                                    "0"
                                ) *
                                  parseFloat(
                                    selectedVehicle?.Vehicle?.payNowDis
                                  )) /
                                  100 || 0,
                          price: totalPrice,
                          selectedInsurance: selectedFields?.insurance,
                          type:
                            userId != null && userId != "null" && userId != ""
                              ? reservedAreaPayNow == "true"
                                ? "PAYNOW"
                                : "PAYLATER"
                              : "",
                          isPayLaterFee: false,
                          payLaterFeeAmt: "",
                          extrasPrice: extrasPrice,
                          extrasIncluded: true,
                          ...(cardPaymentChoice && {
                            type: cardPaymentChoice.type,
                            price: cardPaymentChoice.price,
                            isDiscounted: cardPaymentChoice.isDiscounted,
                            surchargeAmt: cardPaymentChoice.surchargeAmt,
                          }),
                        },
                        vehicleDetails: selectedVehicle,
                      })
                    );
                    goToStepTwo();
                  }
                  setSelectedPrice(totalPrice);
                }}
              />
            )}
            {(!appSettings?.donotShowInsurance &&
              selectedFields.options != null) ||
            (!appSettings?.donotShowExtras &&
              selectedFields.options != null) ? (
              <div
                className="flex flex-col gap-4 items-center justify-center"
                style={{
                  marginTop: isGretaBookingNewUi ? "0px" : getMarginTop(),
                  marginBottom: "19px",
                  padding: isGretaBookingNewUi ? "32px" : "0px",
                  backgroundColor: isGretaBookingNewUi
                    ? "#f5f5f5"
                    : "transparent",
                }}
              >
                {appSettings?.showProceedButtonTopOfExtraPage && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      height: "70px",
                    }}
                  >
                    <AppButton
                      className="mb-2"
                      style={{
                        background: appSettings?.continueButtonPrimaryColor
                          ? dynamicTheme?.palette["primary"]?.main
                          : "#cccccc",
                        height: isSmallScreen ? "2rem" : "3rem",
                        width: isSmallScreen ? "10rem" : "15rem",
                        padding: isSmallScreen ? "8px 0px" : "8px 0px",
                        borderRadius: `${
                          appSettings?.buttonBorderRadius || 0
                        }px`,
                      }}
                      onClick={() => {
                        if (shouldShowNoCoverageWarning()) {
                          pendingProceedTypeRef.current = "";
                          setShowNoCoverageWarning(true);
                          return;
                        }
                        scrollToTop();
                        window.scrollTo(0, 0);
                        if (selectedVehicle) {
                          dispatch(
                            BOOKING_ACTIONS.setSelectedVehicleDetails({
                              paymentDetails: {
                                addedExtraOptions: selectedFields?.options,
                                isDiscounted:
                                  searchParams.get(
                                    "reservationSourcePayLater"
                                  ) === "true" ||
                                  (userId != null &&
                                    userId != "null" &&
                                    userId != "")
                                    ? 0
                                    : (parseFloat(
                                        (selectedVehicle?.Reference?.ID &&
                                        selectedVehicle?.Reference?.ID > 0
                                          ? (
                                              selectedVehicle?.Reference
                                                ?.originalAmount -
                                              selectedVehicle?.Reference.ID
                                            ).toString()
                                          : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) ||
                                          "0"
                                      ) *
                                        parseFloat(
                                          selectedVehicle?.Vehicle?.payNowDis
                                        )) /
                                        100 || 0,
                                price: totalPrice,
                                selectedInsurance: selectedFields?.insurance,
                                type: "",
                                isPayLaterFee: false,
                                payLaterFeeAmt: "",
                                extrasPrice: extrasPrice,
                                extrasIncluded: true,
                                ...(cardPaymentChoice && {
                                  type: cardPaymentChoice.type,
                                  price: cardPaymentChoice.price,
                                  isDiscounted: cardPaymentChoice.isDiscounted,
                                  surchargeAmt: cardPaymentChoice.surchargeAmt,
                                }),
                              },
                              vehicleDetails: selectedVehicle,
                            })
                          );
                          goToStepTwo();
                        }
                        setSelectedPrice(totalPrice);
                      }}
                    >
                      <span>{translate("Continue")}</span>
                    </AppButton>
                  </div>
                )}

                {isInsuranceLoading ? (
                  <CardListSkeleton />
                ) : (
                  !appSettings?.donotShowInsurance &&
                  insuranceList["Booking Insurance"]?.length > 0 &&
                  (isGretaBookingNewUi ? (
                    /* ---- Greta New UI: insurance cards ---- */
                    <div ref={insuranceRef} style={{ width: "100%" }}>
                      <GretaInsuranceCardsLayout
                        selectedInsuranceIndex={insuranceList[
                          "Booking Insurance"
                        ].findIndex(
                          (e) =>
                            e.Description.toLowerCase() ===
                            selectedFields.insurance?.details.toLowerCase()
                        )}
                        onTriggerFindOutMoreUpdate={() =>
                          setIsFindMoreTriggeredInInsComp((prev) => !prev)
                        }
                        onSelected={(e: number) => {
                          const index = e;
                          setSelectedFields((prev) => {
                            if (index < 0) {
                              delete prev.insurance;
                            } else {
                              prev.insurance = {
                                details:
                                  insuranceList["Booking Insurance"][index]
                                    .Description,
                                insuranceId:
                                  insuranceList["Booking Insurance"][
                                    index
                                  ].Id.toString(),
                                price: parseFloat(
                                  parseFloat(
                                    insuranceList["Booking Insurance"][
                                      index
                                    ].Amount.toString()
                                  ).toFixed(2)
                                ),
                              };
                            }
                            return { ...prev };
                          });
                        }}
                        insurances={
                          insuranceList["Booking Insurance"]?.map((e) => ({
                            name: e.Description,
                            isIncluded: isCoverageIncluded(e),
                            pricePerDayInUSD: e.Amount.toString(),
                            features: e.Specification,
                            isRecommended: e.isRecommended,
                            bookingText: e.bookingText,
                            indicator: e.indicator,
                            insuranceRating: e.insuranceRating,
                            isDailyPrice: e.isDailyPrice,
                            isNegotiableDaily: e.isNegotiableDaily,
                          })) || []
                        }
                        secondaryColor={
                          dynamicTheme?.palette["secondaryColor"]?.main
                        }
                        cardBackgroundColor={
                          dynamicTheme?.palette["backgroundColor"]?.main
                        }
                        selectedCardBackgroundColor={
                          dynamicTheme?.palette["ternaryColor"]?.main
                        }
                        cardTextColor={
                          dynamicTheme?.palette["backgroundColor"]?.contrastText
                        }
                        selectedCardTextColor={
                          dynamicTheme?.palette["ternaryColor"]?.contrastText
                        }
                        fillToCard={appSettings?.fillColorToCard}
                        selectedGroup={
                          selectedVehicle?.Vehicle?.VehMakeModel?.Name
                        }
                        insuranceBody={findInsuranceBody}
                        vehicleGroupName={groupName}
                        selectedInsurance={selectedFields.insurance}
                        insurancespec={insuranceList["Specification"]}
                      />
                    </div>
                  ) : (
                    <div
                      ref={insuranceRef}
                      className="w-full box-border p-6 rounded-md shadow-md"
                      style={{
                        backgroundColor:
                          dynamicTheme?.palette["backgroundColor"].main,
                      }}
                    >
                      <div style={{ marginBottom: "30px" }}>
                        {appSettings?.showInsurancePreview &&
                          includedIns.length > 0 && (
                            <InsurancePreview
                              insurances={includedIns}
                              specification={insuranceList["Booking Insurance"]}
                              updateShowInsurance={updateShowInsurance}
                              previewModal={"includedInsurance"}
                              insurancespec={insuranceList["Specification"]}
                            />
                          )}
                      </div>

                      {appSettings?.showUnselectedInsuranceCovers &&
                      appSettings?.showInsurancePreview ? (
                        <>
                          <div style={{ marginBottom: "30px" }}>
                            {showInsurancePreview &&
                              appSettings?.showInsurancePreview &&
                              selectedFields.insurance && (
                                <InsurancePreview
                                  insurance={selectedFields.insurance}
                                  specification={
                                    insuranceList["Booking Insurance"]
                                  }
                                  updateShowInsurance={updateShowInsurance}
                                  previewModal={"selectedInsurance"}
                                  insurancespec={insuranceList["Specification"]}
                                />
                              )}
                          </div>
                          <InsuranceCardsLayout
                            selectedInsuranceIndex={insuranceList[
                              "Booking Insurance"
                            ].findIndex(
                              (e) =>
                                e.Description.toLowerCase() ===
                                selectedFields.insurance?.details.toLowerCase()
                            )}
                            onTriggerFindOutMoreUpdate={() =>
                              setIsFindMoreTriggeredInInsComp((prev) => !prev)
                            }
                            onSelected={(e: number) => {
                              const index = e;
                              appSettings?.showInsurancePreview &&
                                setShowInsurancePreview(true);
                              setSelectedFields((prev) => {
                                if (index < 0) {
                                  delete prev.insurance;
                                } else {
                                  const selectedInsurance =
                                    insuranceList["Booking Insurance"]?.[index];
                                  if (selectedInsurance) {
                                    prev.insurance = {
                                      details: selectedInsurance.Description,
                                      insuranceId:
                                        selectedInsurance.Id.toString(),
                                      price: parseFloat(
                                        parseFloat(
                                          selectedInsurance.Amount.toString()
                                        ).toFixed(2)
                                      ),
                                    };
                                  }
                                }
                                return {
                                  ...prev,
                                };
                              });
                            }}
                            insurances={
                              insuranceList["Booking Insurance"]?.map((e) => {
                                return {
                                  name: e.Description,
                                  isIncluded: isCoverageIncluded(e),
                                  pricePerDayInUSD: e.Amount.toString(),
                                  features: e.Specification,
                                  isRecommended: e.isRecommended,
                                  bookingText: e.bookingText,
                                  indicator: e.indicator,
                                  isDailyPrice: e.isDailyPrice,
                                  isNegotiableDaily: e.isNegotiableDaily,
                                  insuranceRating: e.insuranceRating,
                                };
                              }) || []
                            }
                            secondaryColor={
                              dynamicTheme?.palette["secondaryColor"]?.main
                            }
                            cardBackgroundColor={
                              dynamicTheme?.palette["backgroundColor"]?.main
                            }
                            selectedCardBackgroundColor={
                              dynamicTheme?.palette["ternaryColor"]?.main
                            }
                            cardTextColor={
                              dynamicTheme?.palette["backgroundColor"]
                                ?.contrastText
                            }
                            selectedCardTextColor={
                              dynamicTheme?.palette["ternaryColor"]
                                ?.contrastText
                            }
                            fillToCard={appSettings?.fillColorToCard}
                            selectedGroup={
                              selectedVehicle?.Vehicle?.VehMakeModel?.Name
                            }
                            insuranceBody={findInsuranceBody}
                            vehicleGroupName={groupName}
                            selectedInsurance={selectedFields.insurance}
                            insurancespec={insuranceList["Specification"]}
                          />
                        </>
                      ) : showInsurancePreview &&
                        appSettings?.showInsurancePreview &&
                        selectedFields.insurance ? (
                        <InsurancePreview
                          insurance={selectedFields.insurance}
                          specification={insuranceList["Booking Insurance"]}
                          updateShowInsurance={updateShowInsurance}
                          previewModal={"selectedInsurance"}
                          insurancespec={insuranceList["Specification"]}
                        />
                      ) : (
                        <InsuranceCardsLayout
                          selectedInsuranceIndex={insuranceList[
                            "Booking Insurance"
                          ].findIndex(
                            (e) =>
                              e.Description.toLowerCase() ===
                              selectedFields.insurance?.details.toLowerCase()
                          )}
                          onTriggerFindOutMoreUpdate={() =>
                            setIsFindMoreTriggeredInInsComp((prev) => !prev)
                          }
                          onSelected={(e: number) => {
                            const index = e;
                            appSettings?.showInsurancePreview &&
                              setShowInsurancePreview(true);
                            setSelectedFields((prev) => {
                              if (index < 0) {
                                delete prev.insurance;
                              } else {
                                const selectedInsurance =
                                  insuranceList["Booking Insurance"]?.[index];
                                if (selectedInsurance) {
                                  prev.insurance = {
                                    details: selectedInsurance.Description,
                                    insuranceId:
                                      selectedInsurance.Id.toString(),
                                    price: parseFloat(
                                      parseFloat(
                                        selectedInsurance.Amount.toString()
                                      ).toFixed(2)
                                    ),
                                  };
                                }
                              }
                              return {
                                ...prev,
                              };
                            });
                          }}
                          insurances={
                            insuranceList["Booking Insurance"]?.map((e) => {
                              return {
                                name: e.Description,
                                isIncluded: isCoverageIncluded(e),
                                pricePerDayInUSD: e.Amount.toString(),
                                features: e.Specification,
                                isRecommended: e.isRecommended,
                                bookingText: e.bookingText,
                                indicator: e.indicator,
                                isDailyPrice: e.isDailyPrice,
                                isNegotiableDaily: e.isNegotiableDaily,
                                insuranceRating: e.insuranceRating,
                              };
                            }) || []
                          }
                          secondaryColor={
                            dynamicTheme?.palette["secondaryColor"]?.main
                          }
                          cardBackgroundColor={
                            dynamicTheme?.palette["backgroundColor"]?.main
                          }
                          selectedCardBackgroundColor={
                            dynamicTheme?.palette["ternaryColor"]?.main
                          }
                          cardTextColor={
                            dynamicTheme?.palette["backgroundColor"]
                              ?.contrastText
                          }
                          selectedCardTextColor={
                            dynamicTheme?.palette["ternaryColor"]?.contrastText
                          }
                          fillToCard={appSettings?.fillColorToCard}
                          selectedGroup={
                            selectedVehicle?.Vehicle?.VehMakeModel?.Name
                          }
                          insuranceBody={findInsuranceBody}
                          vehicleGroupName={groupName}
                          selectedInsurance={selectedFields.insurance}
                          insurancespec={insuranceList["Specification"]}
                        />
                      )}
                      {/*{showInsurancePreview &&*/}
                      {/*appSettings?.showInsurancePreview &&*/}
                      {/*selectedFields.insurance ? (*/}
                      {/*  <InsurancePreview*/}
                      {/*    insurance={selectedFields.insurance}*/}
                      {/*    specification={insuranceList["Booking Insurance"]}*/}
                      {/*    updateShowInsurance={updateShowInsurance}*/}
                      {/*    previewModal={"selectedInsurance"}*/}
                      {/*  />*/}
                      {/*) : (*/}
                      {/*  <InsuranceCardsLayout*/}
                      {/*    selectedInsuranceIndex={insuranceList[*/}
                      {/*      "Booking Insurance"*/}
                      {/*    ].findIndex(*/}
                      {/*      (e) =>*/}
                      {/*        e.Description.toLowerCase() ===*/}
                      {/*        selectedFields.insurance?.details.toLowerCase()*/}
                      {/*    )}*/}
                      {/*    onTriggerFindOutMoreUpdate={() =>*/}
                      {/*      setIsFindMoreTriggeredInInsComp((prev) => !prev)*/}
                      {/*    }*/}
                      {/*    onSelected={(e: number) => {*/}
                      {/*      const index = e;*/}
                      {/*      appSettings?.showInsurancePreview &&*/}
                      {/*        setShowInsurancePreview(true);*/}
                      {/*      setSelectedFields((prev) => {*/}
                      {/*        if (index < 0) {*/}
                      {/*          delete prev.insurance;*/}
                      {/*        } else {*/}
                      {/*          let updatedInsuranceList = insuranceList;*/}
                      {/*          if (appSettings?.showInsurancePreview) {*/}
                      {/*            updatedInsuranceList = {*/}
                      {/*              ...insuranceList,*/}
                      {/*              "Booking Insurance": insuranceList[*/}
                      {/*                "Booking Insurance"*/}
                      {/*              ].filter(*/}
                      {/*                (insurance) => insurance !== includedIns*/}
                      {/*              ),*/}
                      {/*            };*/}
                      {/*          }*/}
                      {/*          prev.insurance = {*/}
                      {/*            details:*/}
                      {/*              updatedInsuranceList["Booking Insurance"][*/}
                      {/*                index*/}
                      {/*              ].Description,*/}
                      {/*            insuranceId:*/}
                      {/*              updatedInsuranceList["Booking Insurance"][*/}
                      {/*                index*/}
                      {/*              ].Id.toString(),*/}
                      {/*            price: parseFloat(*/}
                      {/*              parseFloat(*/}
                      {/*                updatedInsuranceList["Booking Insurance"][*/}
                      {/*                  index*/}
                      {/*                ].Amount.toString()*/}
                      {/*              ).toFixed(2)*/}
                      {/*            ),*/}
                      {/*          };*/}
                      {/*        }*/}
                      {/*        return {*/}
                      {/*          ...prev,*/}
                      {/*        };*/}
                      {/*      });*/}
                      {/*    }}*/}
                      {/*    insurances={*/}
                      {/*      insuranceList["Booking Insurance"]?.map((e) => {*/}
                      {/*        return {*/}
                      {/*          name: e.Description,*/}
                      {/*          pricePerDayInUSD: e.Amount.toString(),*/}
                      {/*          features: e.Specification,*/}
                      {/*          isRecommended: e.isRecommended,*/}
                      {/*          bookingText: e.bookingText,*/}
                      {/*        };*/}
                      {/*      }) || []*/}
                      {/*    }*/}
                      {/*    secondaryColor={*/}
                      {/*      dynamicTheme?.palette["secondaryColor"]?.main*/}
                      {/*    }*/}
                      {/*    cardBackgroundColor={*/}
                      {/*      dynamicTheme?.palette["backgroundColor"]?.main*/}
                      {/*    }*/}
                      {/*    selectedCardBackgroundColor={*/}
                      {/*      dynamicTheme?.palette["ternaryColor"]?.main*/}
                      {/*    }*/}
                      {/*    cardTextColor={*/}
                      {/*      dynamicTheme?.palette["backgroundColor"]*/}
                      {/*        ?.contrastText*/}
                      {/*    }*/}
                      {/*    selectedCardTextColor={*/}
                      {/*      dynamicTheme?.palette["ternaryColor"]?.contrastText*/}
                      {/*    }*/}
                      {/*    fillToCard={appSettings?.fillColorToCard}*/}
                      {/*    selectedGroup={*/}
                      {/*      selectedVehicle?.Vehicle?.VehMakeModel?.Name*/}
                      {/*    }*/}
                      {/*    insuranceBody={findInsuranceBody}*/}
                      {/*    vehicleGroupName={groupName}*/}
                      {/*  />*/}
                      {/*)}*/}
                    </div>
                  ))
                )}
                {!appSettings?.donotShowExtras &&
                  selectedVehicle?.optionals &&
                  selectedVehicle?.optionals.length > 0 && (
                    <div className="w-full">
                      {isGretaBookingNewUi && (
                        <h2
                          style={{
                            fontWeight: "900",
                            fontSize: isSmallScreen ? "18px" : "28px",
                            color: "#111",
                            margin: 0,
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                          }}
                        >
                          {translate("WHAT EXTRA DO YOU NEED?")}
                        </h2>
                      )}
                      <div ref={selectVehicleExtrasRef}>
                        <SelectVehicleExtras
                          selectedInsurance={selectedFields?.insurance}
                          values={selectedFields.options || {}}
                          onChange={(e: any) => {
                            setSelectedFields((prev) => {
                              return {
                                ...prev,
                                // abc: 1, implement the logic for automatically adding age related extras
                                options: e,
                              };
                            });
                          }}
                          insuranceList={insuranceList}
                          options={
                            selectedVehicle?.optionals?.map((e) => {
                              return {
                                ...e,
                                iconUrl: e.Equipment.optionalImage || "#",
                                maxAllowed: e.Equipment.isMultipliable
                                  ? e.Equipment.maxQuantity
                                    ? e.Equipment.maxQuantity
                                    : 20
                                  : 1,
                                optionName: e.Equipment?.EquipType || "",
                                pricePerDayInUSD: (
                                  e.Charge.Amount || 0
                                ).toString(),
                                details: e.Equipment?.Description || "",
                                donotShowOptional:
                                  e.Equipment?.donotShowOptional == true
                                    ? true
                                    : false,
                                isDailyPrice:
                                  e?.Equipment?.isDailyPrice == true
                                    ? true
                                    : false,
                                isNegotiableDaily:
                                  e?.Equipment?.isNegotiableDaily == true
                                    ? true
                                    : false,
                              };
                            }) || []
                          }
                          cardBackgroundColor={
                            appSettings?.fillColorToCard
                              ? dynamicTheme?.palette["backgroundColor"]?.main
                              : dynamicTheme?.palette["secondaryColor"]?.main
                          }
                          selectedCardBackgroundColor={
                            dynamicTheme?.palette["primary"]?.main
                          }
                          cardTextColor={
                            appSettings?.fillColorToCard
                              ? dynamicTheme?.palette["backgroundColor"]
                                  ?.contrastText
                              : dynamicTheme?.palette["secondaryColor"]
                                  ?.contrastText
                          }
                          selectedCardTextColor={
                            dynamicTheme?.palette["primary"]?.contrastText
                          }
                          secondaryColor={
                            dynamicTheme?.palette["secondaryColor"]?.main
                          }
                          fillToCard={appSettings?.fillColorToCard}
                        />
                      </div>
                    </div>
                  )}
                {!appSettings?.showProceedButtonTopOfExtraPage &&
                  !isGretaBookingNewUi && (
                    <AppButton
                      className="mb-2"
                      style={{
                        background: appSettings?.continueButtonPrimaryColor
                          ? dynamicTheme?.palette["primary"]?.main
                          : "#cccccc",
                        height: isSmallScreen ? "2rem" : "3rem",
                        width: isSmallScreen ? "6rem" : "8rem",
                        padding: isSmallScreen ? "8px 0px" : "8px 0px",
                        borderRadius: `${
                          appSettings?.buttonBorderRadius || 0
                        }px`,
                      }}
                      onClick={() => {
                        if (shouldShowNoCoverageWarning()) {
                          pendingProceedTypeRef.current =
                            userId != null && userId != "null" && userId != ""
                              ? reservedAreaPayNow == "true"
                                ? "PAYNOW"
                                : "PAYLATER"
                              : "";
                          setShowNoCoverageWarning(true);
                          return;
                        }
                        scrollToTop();
                        window.scrollTo(0, 0);
                        if (selectedVehicle) {
                          dispatch(
                            BOOKING_ACTIONS.setSelectedVehicleDetails({
                              paymentDetails: {
                                addedExtraOptions: selectedFields?.options,
                                isDiscounted:
                                  searchParams.get(
                                    "reservationSourcePayLater"
                                  ) === "true" ||
                                  (userId != null &&
                                    userId != "null" &&
                                    userId != "")
                                    ? 0
                                    : (parseFloat(
                                        (selectedVehicle?.Reference?.ID &&
                                        selectedVehicle?.Reference?.ID > 0
                                          ? (
                                              selectedVehicle?.Reference
                                                ?.originalAmount -
                                              selectedVehicle?.Reference.ID
                                            ).toString()
                                          : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) ||
                                          "0"
                                      ) *
                                        parseFloat(
                                          selectedVehicle?.Vehicle?.payNowDis
                                        )) /
                                        100 || 0,
                                price: totalPrice,
                                selectedInsurance: selectedFields?.insurance,
                                type:
                                  userId != null &&
                                  userId != "null" &&
                                  userId != ""
                                    ? reservedAreaPayNow == "true"
                                      ? "PAYNOW"
                                      : "PAYLATER"
                                    : "",
                                isPayLaterFee: false,
                                payLaterFeeAmt: "",
                                extrasPrice: extrasPrice,
                                extrasIncluded: true,
                                ...(cardPaymentChoice && {
                                  type: cardPaymentChoice.type,
                                  price: cardPaymentChoice.price,
                                  isDiscounted: cardPaymentChoice.isDiscounted,
                                  surchargeAmt: cardPaymentChoice.surchargeAmt,
                                }),
                              },
                              vehicleDetails: selectedVehicle,
                            })
                          );
                          goToStepTwo();
                        }
                        setSelectedPrice(totalPrice);
                      }}
                    >
                      <span>{translate("Continue")}</span>
                    </AppButton>
                  )}
              </div>
            ) : (
              <p className="flex flex-col gap-4 items-center justify-center text-[#000000]">
                No Insurance/Extras Available
                {!appSettings?.showProceedButtonTopOfExtraPage && (
                  <AppButton
                    className="w-fit mb-2"
                    color="secondaryColor"
                    onClick={() => {
                      scrollToTop();
                      window.scrollTo(0, 0);
                      if (selectedVehicle) {
                        dispatch(
                          BOOKING_ACTIONS.setSelectedVehicleDetails({
                            paymentDetails: {
                              addedExtraOptions: selectedFields?.options,
                              isDiscounted:
                                searchParams.get(
                                  "reservationSourcePayLater"
                                ) === "true" ||
                                (userId != null &&
                                  userId != "null" &&
                                  userId != "")
                                  ? 0
                                  : (parseFloat(
                                      (selectedVehicle?.Reference?.ID &&
                                      selectedVehicle?.Reference?.ID > 0
                                        ? (
                                            selectedVehicle?.Reference
                                              ?.originalAmount -
                                            selectedVehicle?.Reference.ID
                                          ).toString()
                                        : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) ||
                                        "0"
                                    ) *
                                      parseFloat(
                                        selectedVehicle?.Vehicle?.payNowDis
                                      )) /
                                      100 || 0,
                              price: totalPrice,
                              selectedInsurance: selectedFields?.insurance,
                              isPayLaterFee: false,
                              payLaterFeeAmt: "",
                              type: "",
                              extrasPrice: extrasPrice,
                              extrasIncluded: true,
                              ...(cardPaymentChoice && {
                                type: cardPaymentChoice.type,
                                price: cardPaymentChoice.price,
                                isDiscounted: cardPaymentChoice.isDiscounted,
                                surchargeAmt: cardPaymentChoice.surchargeAmt,
                              }),
                            },
                            vehicleDetails: selectedVehicle,
                          })
                        );
                        goToStepTwo();
                      }
                      setSelectedPrice(totalPrice);
                    }}
                    style={{
                      borderRadius: `${AppSettings?.buttonBorderRadius || 0}px`,
                    }}
                  >
                    Continue
                  </AppButton>
                )}
              </p>
            )}
          </div>
        )}
        {activeStep === 2 && showUpgradeProposals && hasUpgradeProposals && (
          <div
            className="h-full w-full animate__animated animate__slideInUp"
            style={{
              marginTop: isGretaBookingNewUi ? 0 : payMarginTop(),
            }}
          >
            {isGretaBookingNewUi && (
              <GretaHeader
                title={translate("Upgrade offers")}
                totalPrice={totalPrice ?? 0}
                currency={defaultCurrency || "€"}
                onBack={() => {
                  setSelectedCancellationUpgrade(false);
                  setSelectedVehicleUpgradeChoice(false);
                  setSelectedProtectionUpgrade(false);
                  setSelectedFields((prevFields) => {
                    const options = { ...(prevFields.options || {}) };
                    const cancellationCode =
                      upgradeProposals?.cancellationUpgrade?.optionalCode;
                    const vehicleUpgradeCode =
                      upgradeProposals?.vehicleUpgrade?.optionalCode;
                    if (cancellationCode) delete options[cancellationCode];
                    if (vehicleUpgradeCode) delete options[vehicleUpgradeCode];
                    const next: ChosenOptions = { ...prevFields, options };
                    if (originalInsuranceBeforeUpgradeRef.current) {
                      next.insurance =
                        originalInsuranceBeforeUpgradeRef.current;
                    } else {
                      delete next.insurance;
                    }
                    return next;
                  });
                  setActiveStep(1);
                  setPushState(1);
                  setShowUpgradeProposals(false);
                }}
                onPriceDetailClick={() => setShowPriceDetail(true)}
                translate={translate}
                priceDetailsText={priceDetailsText}
                showContinueButton={true}
                continueButtonStyle={{
                  background: appSettings?.continueButtonPrimaryColor
                    ? dynamicTheme?.palette["primary"]?.main
                    : "",
                  height: "48px",
                  minWidth: "120px",
                  padding: "8px 24px",
                  borderRadius: `${appSettings?.buttonBorderRadius ?? 50}px`,
                }}
                onContinue={handleUpgradeContinue}
              />
            )}
            <UpgradeProposals
              cancellationUpgrade={resolvedCancellationUpgrade}
              vehicleUpgrade={resolvedVehicleUpgrade}
              protectionUpgrade={resolvedProtectionUpgrade}
              selectedCancellation={selectedCancellationUpgrade}
              selectedVehicleUpgrade={selectedVehicleUpgradeChoice}
              selectedProtection={selectedProtectionUpgrade}
              onToggleCancellation={() => {
                setSelectedCancellationUpgrade((prev) => {
                  const next = !prev;
                  const cancellationUpgrade = resolvedCancellationUpgrade;
                  if (cancellationUpgrade) {
                    setSelectedFields((prevFields) => {
                      const options = { ...(prevFields.options || {}) };
                      if (next) {
                        options[cancellationUpgrade.optionalCode] = {
                          ...(options[cancellationUpgrade.optionalCode] || {}),
                          name: cancellationUpgrade.optionalCode,
                          details: translate("Cancellation upgrade"),
                          price: cancellationUpgrade.price,
                          quantity: 1,
                          doNotShowOptionalPrice: false,
                        } as SelectedVehicleExtraOptions;
                      } else {
                        delete options[cancellationUpgrade.optionalCode];
                      }
                      return { ...prevFields, options };
                    });
                  }
                  return next;
                });
              }}
              onToggleVehicleUpgrade={() => {
                setSelectedVehicleUpgradeChoice((prev) => {
                  const next = !prev;
                  const optionalCode =
                    upgradeProposals?.vehicleUpgrade?.optionalCode;
                  if (topVehicleCandidate && optionalCode) {
                    setSelectedFields((prevFields) => {
                      const options = { ...(prevFields.options || {}) };
                      if (next) {
                        const vehicleUpgradeQuantity = upgradeProposals
                          ?.vehicleUpgrade?.isDailyPrice
                          ? rentalDays(
                              ReservationDetails.pickUpDateAndTime,
                              ReservationDetails.dropOffDateAndTime
                            ) || 1
                          : 1;
                        options[optionalCode] = {
                          ...(options[optionalCode] || {}),
                          name: optionalCode,
                          details: `${translate("Vehicle upgrade")} (${
                            topVehicleCandidate.groupCode
                          })`,
                          price: topVehicleCandidate.price,
                          quantity: vehicleUpgradeQuantity,
                          isVehicleUpgrade: true,
                          vehicleUpgradeGroupCode:
                            topVehicleCandidate.groupCode,
                          doNotShowOptionalPrice: false,
                        } as SelectedVehicleExtraOptions;
                      } else {
                        delete options[optionalCode];
                      }
                      return { ...prevFields, options };
                    });
                  }
                  return next;
                });
              }}
              onToggleProtection={() => {
                setSelectedProtectionUpgrade((prev) => {
                  const next = !prev;
                  if (matchingProtectionInsurance) {
                    setSelectedFields((prevFields) => {
                      const nextFields: ChosenOptions = { ...prevFields };
                      if (next) {
                        nextFields.insurance = {
                          details: matchingProtectionInsurance.Description,
                          insuranceId:
                            matchingProtectionInsurance.Id.toString(),
                          price: parseFloat(
                            matchingProtectionInsurance.Amount.toString()
                          ),
                        };
                      } else if (originalInsuranceBeforeUpgradeRef.current) {
                        nextFields.insurance =
                          originalInsuranceBeforeUpgradeRef.current;
                      } else {
                        delete nextFields.insurance;
                      }
                      return nextFields;
                    });
                  }
                  return next;
                });
              }}
              currencySymbol={defaultCurrency || "€"}
              onContinue={handleUpgradeContinue}
              hideTitle={isGretaBookingNewUi}
              hideFooterButtons={isGretaBookingNewUi}
            />
          </div>
        )}
        {activeStep === 2 && !showUpgradeProposals && (
          <div
            className="h-full w-full animate__animated animate__slideInUp"
            style={{
              marginTop: isGretaBookingNewUi ? 0 : payMarginTop(),
            }}
          >
            <PaymentDetailsPage
              reservationSourcePayNow={reservationSourcePayNow}
              reservationSourcePayLater={reservationSourcePayLater}
              updateVehiclePrice={updateVehiclePrice}
              updateSelectedPrice={updateSelectedPrice}
              updateVehiclePriceLater={updateVehiclePriceLater}
              allPayLaterMethods={allPayLaterMethods}
              allPayNowMethods={allPayNowMethods}
              updateActiveStep={updateActiveStep}
              isAirport={isAirport}
              showScalapayWidgetPayLater={showScalapayWidgetPayLater}
              showScalapayWidgetPayNow={showScalapayWidgetPayNow}
              scalapayMerchantToken={scalapayMerchantToken}
              scalapayEnvVal={scalapayEnvVal}
              localeScalapay={localeScalapay}
              paymentType={paymentType}
              onPaymentTypeChange={setPaymentType}
              onBack={() => {
                if (hasUpgradeProposals) {
                  upgradeStepCompletedRef.current = false;
                  setShowUpgradeProposals(true);
                } else if (hideInsExtrasThirdTab) {
                  setActiveStep(0);
                  setPushState(0);
                } else {
                  setActiveStep(1);
                  setPushState(1);
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Price Detail Modal */}
      <Dialog
        open={showPriceDetail}
        onClose={() => setShowPriceDetail(false)}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            paddingTop: "100px",
          },
        }}
        PaperProps={{
          style: { borderRadius: 16, minWidth: 320, maxWidth: 480 },
        }}
      >
        <div className="p-6 relative">
          {/* Close button */}
          <button
            onClick={() => setShowPriceDetail(false)}
            style={{
              position: "absolute",
              top: 3,
              right: 12,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "35px",
              lineHeight: 1,
              color: "#666",
            }}
          >
            ×
          </button>
          {/* Header */}
          <div className="mb-5">
            <h2 className="font-bold text-xl uppercase tracking-wide">
              {priceDetailsText}
            </h2>
          </div>

          {/* Costi di noleggio */}
          <p className="font-bold text-sm text-black mb-2">
            {translate("Rental costs")}
          </p>
          <div className="flex justify-between text-sm text-gray-700 mb-1">
            <span>{translate("Totale Noleggio:")}</span>
            <span>
              {vehiclePrice.toFixed(2)}&nbsp;{defaultCurrency || "€"}
            </span>
          </div>

          {/* Insurance */}
          {!appSettings?.donotShowInsurance && insurancePrice > 0 && (
            <div className="flex justify-between text-sm text-gray-700 mb-1">
              <span>
                {selectedFields?.insurance?.details ||
                  translate("Assicurazione")}
              </span>
              <span>
                {insurancePrice.toFixed(2)}&nbsp;{defaultCurrency || "€"}
              </span>
            </div>
          )}

          {/* Extras */}
          {!appSettings?.donotShowExtras && onlyExtrasPrice > 0 && (
            <>
              <p className="font-bold text-sm text-black mt-4 mb-2">
                {translate("Extras")}
              </p>
              {Object.values(
                (selectedFields?.options as Record<
                  string,
                  { name: string; price: number; quantity: number }
                >) || {}
              ).map((extra, i) => (
                <div
                  key={i}
                  className="flex justify-between text-sm text-gray-700 mb-1"
                >
                  <span>{extra.name}</span>
                  <span>
                    {(extra.price * extra.quantity).toFixed(2)}&nbsp;
                    {defaultCurrency || "€"}
                  </span>
                </div>
              ))}
            </>
          )}

          {/* Total */}
          <hr style={{ margin: "16px 0", borderColor: "#e5e7eb" }} />
          <div className="flex justify-between font-bold text-base text-black">
            <span>{translate("Total")}</span>
            <span>
              {(totalPrice ?? 0).toFixed(2)}&nbsp;{defaultCurrency || "€"}
            </span>
          </div>
        </div>
      </Dialog>
      <NoCoverageWarningDialog
        open={showNoCoverageWarning}
        onClose={() => setShowNoCoverageWarning(false)}
        onContinueWithoutCoverage={() => {
          setShowNoCoverageWarning(false);
          const type = pendingProceedTypeRef.current;
          scrollToTop();
          window.scrollTo(0, 0);
          if (selectedVehicle) {
            dispatch(
              BOOKING_ACTIONS.setSelectedVehicleDetails({
                paymentDetails: {
                  addedExtraOptions: selectedFields?.options,
                  isDiscounted:
                    searchParams.get("reservationSourcePayLater") === "true" ||
                    (userId != null && userId != "null" && userId != "")
                      ? 0
                      : (parseFloat(
                          (selectedVehicle?.Reference?.ID &&
                          selectedVehicle?.Reference?.ID > 0
                            ? (
                                selectedVehicle?.Reference?.originalAmount -
                                selectedVehicle?.Reference.ID
                              ).toString()
                            : selectedVehicle?.TotalCharge?.RateTotalAmount.toString()) ||
                            "0"
                        ) *
                          parseFloat(selectedVehicle?.Vehicle?.payNowDis)) /
                          100 || 0,
                  price: totalPrice,
                  selectedInsurance: selectedFields?.insurance,
                  type,
                  isPayLaterFee: false,
                  payLaterFeeAmt: "",
                  extrasPrice: extrasPrice,
                  extrasIncluded: true,
                  ...(cardPaymentChoice && {
                    type: cardPaymentChoice.type,
                    price: cardPaymentChoice.price,
                    isDiscounted: cardPaymentChoice.isDiscounted,
                    surchargeAmt: cardPaymentChoice.surchargeAmt,
                  }),
                },
                vehicleDetails: selectedVehicle,
              })
            );
            goToStepTwo();
          }
          setSelectedPrice(totalPrice);
        }}
        onAddCoverageAndContinue={() => {
          const candidate = getBestAvailableCoverage();
          if (candidate) {
            appSettings?.showInsurancePreview && setShowInsurancePreview(true);
            setSelectedFields((prev) => ({
              ...prev,
              insurance: {
                details: candidate.Description,
                insuranceId: candidate.Id.toString(),
                price: parseFloat(
                  parseFloat(candidate.Amount.toString()).toFixed(2)
                ),
              },
            }));
          }
          autoProceedAfterAddRef.current = true;
          setShowNoCoverageWarning(false);
        }}
        title={noCoverageWarningTitle}
        body={noCoverageWarningBody}
        continueWithoutCoverageLabel={noCoverageWarningContinueCta}
        addCoverageLabel={noCoverageWarningAddCta.replace(
          "{coverageName}",
          getBestAvailableCoverage()?.Description || ""
        )}
        primaryColor={dynamicTheme?.palette["primary"]?.main}
      />
    </ThemeProvider>
  );
}

export { ChooseVehiclePage };
export function SharedURLHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const data = searchParams.get("data");

    if (data) {
      try {
        const secretKey = process.env.REACT_APP_SECRET_KEY;
        const originalUrl = CryptoJS.AES.decrypt(
          decodeURIComponent(data),
          secretKey
        );
        const decryptedUrl = originalUrl.toString(CryptoJS.enc.Utf8);
        if (decryptedUrl) {
          const url = new URL(decryptedUrl);
          window.location.replace(url.pathname + url.search);
        }
      } catch (error) {
        console.error("Failed to decrypt URL:", error);
        navigate("/"); // Redirect to home on error
      }
    } else {
      navigate("/"); // Redirect to home if no data
    }
  }, [searchParams, navigate]);
  return <div>Redirecting...</div>;
}
